/**
 * ClarCV long SEO guides (batch C). Spanish + English, ~12–16 min reads.
 * No imports; merged elsewhere into the blog catalog.
 */

export default [
  {
    slug: 'linkedin-y-cv',
    date: '2026-08-09',
    category: 'career',
    related: ['adaptar-cv-a-una-oferta', 'linkedin-url-en-el-cv', 'checklist-cv-antes-de-enviar'],
    es: {
      title: 'LinkedIn y CV: cómo alinearlos sin contradicciones',
      description:
        'Guía práctica para que tu perfil de LinkedIn y tu currículum digan lo mismo, refuercen tu marca y eviten que el reclutador desconfíe.',
      readMinutes: 14,
      sections: [
        {
          type: 'p',
          text: 'Muchos candidatos tratan LinkedIn y el CV como documentos separados: uno “comercial” y otro “formal”. El reclutador, en cambio, los mira juntos. Abre tu PDF, busca tu nombre en LinkedIn y compara títulos, fechas y logros. Si no coinciden, nace una duda innecesaria: ¿cuál es la versión verdadera?',
        },
        {
          type: 'p',
          text: 'Alinear ambos no significa copiar pegar el mismo texto. Significa una sola historia profesional contada en dos formatos. El CV es denso, escaneable y pensado para ATS y entrevistas. LinkedIn es más narrativo, buscable y útil para networking. Cuando la historia es coherente, ganas credibilidad y velocidad en el proceso.',
        },
        {
          type: 'h2',
          text: 'Qué debe coincidir sí o sí',
        },
        {
          type: 'p',
          text: 'Hay datos que no admiten “versiones creativas”. Si en el CV eres “Product Manager” y en LinkedIn “Growth Lead”, el hiring manager asumirá inflación de título o falta de claridad. Lo mismo con fechas: un mes de diferencia se entiende; seis meses o un año perdido, no.',
        },
        {
          type: 'ul',
          items: [
            'Nombre completo y forma de contacto profesionales (email serio, LinkedIn limpio).',
            'Títulos de puesto y nombres de empresa (misma ortografía y marca).',
            'Mes/año de inicio y fin en cada experiencia relevante.',
            'Formación principal: título, centro y año (o “en curso”).',
            'Ubicación o modalidad si la mencionas (remoto, híbrido, ciudad).',
          ],
        },
        {
          type: 'p',
          text: 'Antes: CV “Analista Senior”, LinkedIn “Business Intelligence Specialist”, fechas 2021–2023 vs 2020–2023. Después: mismo título principal en ambos, subtítulo o About de LinkedIn para matices (“BI / analytics”), y fechas idénticas. El matiz va en la narrativa, no en los hechos.',
        },
        {
          type: 'h2',
          text: 'Dónde sí pueden diferir (y cómo)',
        },
        {
          type: 'p',
          text: 'El CV debe caber en una o dos páginas: prioriza logros medibles y keywords de la oferta. LinkedIn puede ampliar contexto, proyectos, recomendaciones y publicaciones. Usa el About para tono y motivación; en el CV, un resumen de 3–4 líneas orientado al rol.',
        },
        {
          type: 'p',
          text: 'Ejemplo de diferencia sana: en el CV, tres viñetas de un puesto con métricas. En LinkedIn, esas mismas tres ideas más una frase sobre el equipo, el stack o el aprendizaje. No inventes un cuarto logro solo para LinkedIn; desarrolla el mismo logro con más detalle.',
        },
        {
          type: 'h2',
          text: 'Titular de LinkedIn vs título del CV',
        },
        {
          type: 'p',
          text: 'El titular de LinkedIn no tiene que ser idéntico a la primera línea del CV, pero debe apuntar al mismo mercado. Si buscas roles de “Customer Success Manager”, no uses un titular genérico tipo “Apasionado del servicio y el crecimiento”. Mejor: “Customer Success Manager | SaaS B2B | Retención y onboarding”.',
        },
        {
          type: 'p',
          text: 'En el CV, el resumen profesional hace el trabajo del titular: rol + contexto + valor. Si cambias de objetivo (de ventas a operaciones), actualiza ambos el mismo día. Un LinkedIn desactualizado es la prueba más fácil de que tu candidatura no está cuidada.',
        },
        {
          type: 'h2',
          text: 'Experiencia: misma estructura, distinta densidad',
        },
        {
          type: 'p',
          text: 'Orden cronológico inverso en ambos. En el CV, 3–6 viñetas por rol reciente; roles antiguos, 1–2 o solo línea de título. En LinkedIn puedes dejar más texto, pero evita el muro de párrafo. Usa saltos y bullets si la plataforma lo permite en la descripción del puesto.',
        },
        {
          type: 'p',
          text: 'Error frecuente: pegar el CV entero en LinkedIn. Resultado: ilegible en móvil y pobre en keywords naturales. Otro error: LinkedIn brillante y CV genérico. El recruiter imprime o descarga el CV; si ahí no hay prueba, el About no salva la entrevista.',
        },
        {
          type: 'ul',
          items: [
            'Misma empresa, mismo periodo, mismo título base.',
            'Mismos logros estrella (aunque LinkedIn desarrolle más).',
            'Skills alineadas: si el CV lista Salesforce, LinkedIn también debería reflejarlo.',
            'Proyectos destacados coherentes (portfolio, GitHub, casos).',
          ],
        },
        {
          type: 'h2',
          text: 'Foto, diseño y señales de profesionalidad',
        },
        {
          type: 'p',
          text: 'En España y gran parte de Europa/LATAM, foto en CV es habitual; en LinkedIn casi siempre. Si usas foto en ambos, que sea la misma línea visual: profesional, fondo neutro, ropa coherente con tu sector. No hace falta ser idéntica pixel a pixel, pero sí reconocible.',
        },
        {
          type: 'p',
          text: 'El diseño del CV debe ser ATS-friendly: texto seleccionable, secciones claras, sin tablas frágiles. LinkedIn ya tiene su layout; tu trabajo es el contenido. Si regeneras el CV con plantillas ClarCV, revisa que los datos coincidan con tu perfil público antes de enviar.',
        },
        {
          type: 'h2',
          text: 'Checklist de alineación (15 minutos)',
        },
        {
          type: 'ul',
          items: [
            'Abre CV y LinkedIn en paralelo y compara cada puesto.',
            'Busca tu nombre en Google: ¿sale un perfil viejo o un segundo LinkedIn?',
            'Revisa URL de LinkedIn personalizada y que esté en el CV.',
            'Actualiza el About si el resumen del CV cambió de objetivo.',
            'Pide a alguien que lea ambos 60 segundos: ¿cuenta la misma historia?',
          ],
        },
        {
          type: 'h2',
          text: 'Cuando adaptas el CV a una oferta',
        },
        {
          type: 'p',
          text: 'No necesitas reescribir LinkedIn por cada candidatura. Sí debes evitar que el CV “personalizado” contradiga tu perfil público. Puedes enfatizar distintos logros en el CV (más keywords de la oferta) siempre que esos logros existan también en LinkedIn o sean verificables.',
        },
        {
          type: 'p',
          text: 'Si la oferta pide “español e inglés”, ambos documentos deberían reflejar idiomas. Si piden “experiencia en retail”, el CV puede priorizar ese sector en el resumen; LinkedIn puede mantener un About más amplio, pero no negar el retail si es tu ancla.',
        },
        {
          type: 'h2',
          text: 'Errores que generan desconfianza',
        },
        {
          type: 'ul',
          items: [
            'Títulos inflados solo en LinkedIn (“Head of” cuando eras especialista).',
            'Fechas maquilladas para ocultar gaps sin explicación.',
            'Skills de moda en LinkedIn que no aparecen ni se demuestran en el CV.',
            'Empresa fantasma o freelance sin detalle en ninguno de los dos.',
            'CV en inglés y LinkedIn solo en español (o al revés) sin criterio de mercado.',
          ],
        },
        {
          type: 'p',
          text: 'Antes de una ronda de envíos, sube tu PDF o Word a ClarCV: la IA ayuda a clarificar estructura y logros. Luego cruza el resultado con LinkedIn. Cinco minutos de coherencia evitan treinta minutos de explicación incómoda en entrevista.',
        },
        {
          type: 'h2',
          text: 'Mensajes a reclutadores y el CV adjunto',
        },
        {
          type: 'p',
          text: 'Cuando escribes por LinkedIn y adjuntas el CV (o enlazas el perfil), la primera frase del mensaje debe coincidir con el resumen del archivo. Si en el mensaje te presentas como “analista de datos open to work en fintech” y el PDF habla solo de “consultor junior generalista”, el reclutador siente fricción antes de abrir el resto.',
        },
        {
          type: 'p',
          text: 'Buena práctica: misma keyword de rol en asunto/primera línea, en el resumen del CV y en el titular. Luego una prueba concreta (métrica o proyecto) que también aparezca en experiencia. Ese triángulo mensaje–CV–LinkedIn acelera respuestas porque reduce trabajo cognitivo.',
        },
        {
          type: 'h2',
          text: 'Gaps, promociones internas y títulos “raros”',
        },
        {
          type: 'p',
          text: 'Si tu empresa usaba títulos internos poco estándar (“People Partner L3”, “Associate II”), traduce al mercado en ambos sitios con el mismo mapeo. Ejemplo: en CV y LinkedIn “HR Business Partner (título interno: People Partner L3)”. Así no mientes y no pierdes búsquedas.',
        },
        {
          type: 'p',
          text: 'Para gaps, sé coherente: si en LinkedIn explicas formación o cuidado familiar en el About, no dejes el CV con un agujero mudo de 14 meses. Una línea breve en el resumen o bajo experiencia (“2022: formación intensiva en análisis de datos”) alinea la historia sin dramatizar.',
        },
        {
          type: 'h2',
          text: 'Idioma, mercado y versiones paralelas',
        },
        {
          type: 'p',
          text: 'Si aplicas en España y en roles internacionales, puedes tener CV en ES y EN. LinkedIn admite un perfil principal y un secundario: mantén hechos idénticos y solo cambia el idioma. No uses el perfil inglés para “inflar” seniority que el español no muestra. ClarCV facilita regenerar versiones claras del mismo contenido base en el idioma de cada candidatura.',
        },
        {
          type: 'p',
          text: 'Revisa también recomendaciones y featured: un certificado caducado o un proyecto de 2016 fijado arriba puede contradecir el foco actual del CV. Archiva o reordena destacados para que el visitante vea primero lo alineado a tu objetivo de 2026.',
        },
        {
          type: 'p',
          text: 'Un ritual útil cada trimestre: exporta el CV, ábrelo junto a LinkedIn, y marca en rojo cualquier divergencia de título, fecha o logro estrella. Corrige el mismo día. La deuda de coherencia crece rápido cuando actualizas solo uno de los dos canales tras un cambio de empleo o de objetivo.',
        },
        {
          type: 'p',
          text: 'También conviene auditar las skills endorsements: si LinkedIn te asocia a herramientas que ya no usas, ocúltalas o bórralas para no contradecir el bloque de habilidades del CV. Lo mismo con intereses y banners genéricos que restan seriedad en sectores conservadores.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿El CV y LinkedIn deben tener el mismo texto?',
              a: 'No. Deben compartir hechos (títulos, fechas, logros clave) y objetivo. LinkedIn puede ser más narrativo; el CV, más denso y orientado a la oferta.',
            },
            {
              q: '¿Debo poner el enlace de LinkedIn en el CV?',
              a: 'Sí, si el perfil está actualizado y profesional. Usa una URL personalizada limpia. Si el perfil está incompleto, actualízalo antes de enlazarlo.',
            },
            {
              q: '¿Qué hago si cambié de carrera y LinkedIn muestra el pasado?',
              a: 'Actualiza titular, About y el orden de destacados. En el CV, prioriza transferibles y proyectos del nuevo rumbo. No borres el pasado: reencuádralo.',
            },
            {
              q: '¿Puedo tener un CV más “senior” que mi LinkedIn?',
              a: 'No es buena idea. La inconsistencia se detecta rápido. Alinea el nivel de responsabilidad y deja que entrevistas y referencias confirmen el seniority.',
            },
            {
              q: '¿LinkedIn sustituye al CV?',
              a: 'Casi nunca. La mayoría de procesos piden archivo. LinkedIn abre puertas; el CV cierra el filtro ATS y estructura la entrevista.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'LinkedIn and CV: how to align them without contradictions',
      description:
        'A practical guide so your LinkedIn profile and résumé tell the same story, reinforce your brand, and keep recruiters trusting you.',
      readMinutes: 14,
      sections: [
        {
          type: 'p',
          text: 'Many candidates treat LinkedIn and the CV as separate documents: one “marketing” page and one “formal” file. Recruiters look at both. They open your PDF, search your name on LinkedIn, and compare titles, dates, and achievements. When those do not match, an unnecessary doubt appears: which version is true?',
        },
        {
          type: 'p',
          text: 'Alignment does not mean copy-paste. It means one professional story told in two formats. The CV is dense, scannable, and built for ATS and interviews. LinkedIn is more narrative, searchable, and useful for networking. When the story is consistent, you gain credibility and speed in the process.',
        },
        {
          type: 'h2',
          text: 'What must match without exception',
        },
        {
          type: 'p',
          text: 'Some facts do not allow creative versions. If your CV says “Product Manager” and LinkedIn says “Growth Lead,” hiring managers assume title inflation or unclear thinking. The same applies to dates: a one-month difference is fine; six months or a missing year is not.',
        },
        {
          type: 'ul',
          items: [
            'Full name and professional contact details (serious email, clean LinkedIn URL).',
            'Job titles and company names (same spelling and brand).',
            'Start and end month/year for each relevant role.',
            'Main education: degree, school, and year (or “in progress”).',
            'Location or work mode if you mention it (remote, hybrid, city).',
          ],
        },
        {
          type: 'p',
          text: 'Before: CV “Senior Analyst,” LinkedIn “Business Intelligence Specialist,” dates 2021–2023 vs 2020–2023. After: the same primary title on both, LinkedIn About for nuance (“BI / analytics”), and identical dates. Nuance belongs in the narrative, not in the facts.',
        },
        {
          type: 'h2',
          text: 'Where they can differ (and how)',
        },
        {
          type: 'p',
          text: 'The CV must fit one or two pages: prioritize measurable wins and keywords from the job ad. LinkedIn can expand context, projects, recommendations, and posts. Use About for tone and motivation; on the CV, keep a 3–4 line summary aimed at the role.',
        },
        {
          type: 'p',
          text: 'A healthy difference: on the CV, three bullets with metrics for a role. On LinkedIn, the same three ideas plus a sentence about the team, stack, or learning. Do not invent a fourth achievement only for LinkedIn; deepen the same achievement instead.',
        },
        {
          type: 'h2',
          text: 'LinkedIn headline vs CV title line',
        },
        {
          type: 'p',
          text: 'Your LinkedIn headline does not need to mirror the first line of the CV, but it should point to the same market. If you want “Customer Success Manager” roles, skip vague headlines like “Passionate about service and growth.” Prefer: “Customer Success Manager | B2B SaaS | Retention and onboarding.”',
        },
        {
          type: 'p',
          text: 'On the CV, the professional summary does the headline’s job: role + context + value. If your target changes (sales to operations), update both the same day. An outdated LinkedIn is the easiest signal that your application is not carefully prepared.',
        },
        {
          type: 'h2',
          text: 'Experience: same structure, different density',
        },
        {
          type: 'p',
          text: 'Reverse chronological order on both. On the CV, 3–6 bullets for recent roles; older roles get 1–2 bullets or a title line only. LinkedIn can hold more text, but avoid walls of paragraphs. Use line breaks and bullets in the role description when the platform allows it.',
        },
        {
          type: 'p',
          text: 'Common mistake: pasting the entire CV into LinkedIn. Result: unreadable on mobile and weak natural keywords. Another mistake: a brilliant LinkedIn and a generic CV. Recruiters download the file; if proof is missing there, the About section will not save the interview.',
        },
        {
          type: 'ul',
          items: [
            'Same company, same period, same base title.',
            'Same star achievements (even if LinkedIn expands them).',
            'Aligned skills: if the CV lists Salesforce, LinkedIn should reflect it.',
            'Consistent featured projects (portfolio, GitHub, case studies).',
          ],
        },
        {
          type: 'h2',
          text: 'Photo, design, and professionalism signals',
        },
        {
          type: 'p',
          text: 'In Spain and much of Europe/LATAM, a photo on the CV is common; on LinkedIn it is almost always expected. If you use a photo on both, keep the same visual line: professional, neutral background, clothing that fits your sector. It does not need to be pixel-identical, but it should be recognizable.',
        },
        {
          type: 'p',
          text: 'CV design should be ATS-friendly: selectable text, clear sections, no fragile tables. LinkedIn already owns the layout; your job is the content. If you regenerate the CV with ClarCV templates, cross-check the data against your public profile before you apply.',
        },
        {
          type: 'h2',
          text: 'Alignment checklist (15 minutes)',
        },
        {
          type: 'ul',
          items: [
            'Open the CV and LinkedIn side by side and compare every role.',
            'Google your name: is an old profile or a second LinkedIn showing up?',
            'Check your custom LinkedIn URL and place it on the CV.',
            'Update About if the CV summary changed career direction.',
            'Ask someone to skim both for 60 seconds: do they tell the same story?',
          ],
        },
        {
          type: 'h2',
          text: 'When you tailor the CV to a job ad',
        },
        {
          type: 'p',
          text: 'You do not need to rewrite LinkedIn for every application. You do need to avoid a “customized” CV that contradicts your public profile. You can emphasize different wins on the CV (more keywords from the ad) as long as those wins also exist on LinkedIn or are otherwise verifiable.',
        },
        {
          type: 'p',
          text: 'If the ad asks for Spanish and English, both documents should show languages. If it asks for retail experience, the CV can prioritize that sector in the summary; LinkedIn can keep a broader About, but it should not deny retail if that is your anchor.',
        },
        {
          type: 'h2',
          text: 'Mistakes that create distrust',
        },
        {
          type: 'ul',
          items: [
            'Inflated titles only on LinkedIn (“Head of” when you were a specialist).',
            'Massaged dates to hide gaps with no explanation.',
            'Trendy skills on LinkedIn that never appear or get proven on the CV.',
            'Ghost companies or freelance work with no detail on either side.',
            'English CV and Spanish-only LinkedIn (or the reverse) with no market logic.',
          ],
        },
        {
          type: 'p',
          text: 'Before a round of applications, upload your PDF or Word file to ClarCV: AI helps clarify structure and achievements. Then cross-check the result with LinkedIn. Five minutes of consistency can spare you thirty minutes of awkward interview explanations.',
        },
        {
          type: 'h2',
          text: 'Recruiter messages and the attached CV',
        },
        {
          type: 'p',
          text: 'When you message on LinkedIn and attach the CV (or point to your profile), the first line of the note should match the file summary. If the message says “data analyst open to fintech roles” and the PDF only says “junior generalist consultant,” the recruiter feels friction before reading further.',
        },
        {
          type: 'p',
          text: 'Best practice: the same role keyword in the opening line, the CV summary, and the headline. Then one concrete proof (metric or project) that also appears under experience. That message–CV–LinkedIn triangle speeds replies because it lowers cognitive load.',
        },
        {
          type: 'h2',
          text: 'Gaps, internal promotions, and odd titles',
        },
        {
          type: 'p',
          text: 'If your company used non-standard internal titles (“People Partner L3,” “Associate II”), translate them for the market in both places with the same mapping. Example: on CV and LinkedIn “HR Business Partner (internal title: People Partner L3).” You stay honest and remain searchable.',
        },
        {
          type: 'p',
          text: 'For gaps, stay consistent: if LinkedIn explains training or family care in About, do not leave a silent 14-month hole on the CV. A brief line in the summary or under experience (“2022: intensive data-analysis training”) aligns the story without drama.',
        },
        {
          type: 'h2',
          text: 'Language, market, and parallel versions',
        },
        {
          type: 'p',
          text: 'If you apply in Spain and for international roles, you can keep Spanish and English CVs. LinkedIn supports a primary and secondary profile language: keep facts identical and change only the language. Do not use the English profile to inflate seniority the Spanish one does not show. ClarCV makes it easier to regenerate clear versions of the same base content for each application language.',
        },
        {
          type: 'p',
          text: 'Also review recommendations and featured items: an expired certificate or a 2016 project pinned at the top can contradict your current CV focus. Archive or reorder featured content so visitors see alignment with your 2026 goal first.',
        },
        {
          type: 'p',
          text: 'A useful quarterly ritual: export the CV, open it next to LinkedIn, and mark in red any title, date, or star-achievement divergence. Fix the same day. Coherence debt grows quickly when you update only one channel after a job change or a new target role.',
        },
        {
          type: 'p',
          text: 'Also audit skill endorsements: if LinkedIn associates you with tools you no longer use, hide or remove them so they do not contradict the CV skills block. The same goes for interests and generic banners that reduce seriousness in conservative sectors. One extra pass here often closes the last credibility gaps between profile and file.',
        },
        {
          type: 'p',
          text: 'Finally, treat every job-change week as a synchronization sprint: update title, About, featured work, and the exported CV in the same sitting. Waiting “until later” is how contradictions accumulate and how recruiters catch inconsistencies you forgot you created.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should the CV and LinkedIn use the same wording?',
              a: 'No. They should share facts (titles, dates, key wins) and goal. LinkedIn can be more narrative; the CV denser and tailored to the role.',
            },
            {
              q: 'Should I put my LinkedIn URL on the CV?',
              a: 'Yes, if the profile is updated and professional. Use a clean custom URL. If the profile is incomplete, fix it before linking it.',
            },
            {
              q: 'What if I changed careers and LinkedIn still shows the past?',
              a: 'Update headline, About, and featured items. On the CV, lead with transferables and projects for the new path. Do not erase the past—reframe it.',
            },
            {
              q: 'Can my CV look more senior than my LinkedIn?',
              a: 'That is a bad idea. Inconsistency is easy to spot. Align the responsibility level and let interviews and references confirm seniority.',
            },
            {
              q: 'Does LinkedIn replace the CV?',
              a: 'Almost never. Most processes still ask for a file. LinkedIn opens doors; the CV clears ATS filters and structures the interview.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'plantillas-cv-compatibles-ats',
    date: '2026-08-09',
    category: 'format',
    related: ['cv-canva-y-ats', 'color-y-fuente-cv', 'cv-para-filtros-ats'],
    es: {
      title: 'Plantillas de CV compatibles con ATS: qué buscar y qué evitar',
      description:
        'Cómo elegir una plantilla de currículum que se vea bien ante humanos y se lea bien en filtros ATS, con checklist y errores típicos.',
      readMinutes: 15,
      sections: [
        {
          type: 'p',
          text: 'Una plantilla bonita puede arruinar tu candidatura si el ATS no entiende el archivo. El parser necesita texto real, secciones predecibles y un orden lógico. El reclutador necesita jerarquía visual, aire y logros fáciles de escanear. La plantilla correcta sirve a los dos.',
        },
        {
          type: 'p',
          text: '“Compatible con ATS” no es un sello mágico de Canva. Es un conjunto de decisiones: tipografía, columnas, tablas, iconos, encabezados y exportación. Esta guía te ayuda a evaluar cualquier plantilla —incluida las de ClarCV— antes de enviar cientos de candidaturas con el mismo fallo estructural.',
        },
        {
          type: 'h2',
          text: 'Qué hace que una plantilla sea ATS-friendly',
        },
        {
          type: 'p',
          text: 'El ATS convierte tu archivo en campos: nombre, email, puestos, fechas, skills. Si el diseño rompe ese mapeo, pierdes keywords o peores: el sistema cree que no tienes experiencia. Las plantillas seguras usan flujo de lectura de arriba abajo, encabezados estándar y poco adorno estructural.',
        },
        {
          type: 'ul',
          items: [
            'Texto seleccionable (puedes copiar una frase del PDF).',
            'Secciones con nombres claros: Experiencia, Educación, Habilidades, Idiomas.',
            'Fechas junto al puesto, fáciles de localizar.',
            'Una columna principal de contenido (o dos columnas muy estables).',
            'Sin text boxes flotantes, WordArt ni gráficos como “barras de skill”.',
          ],
        },
        {
          type: 'h2',
          text: 'Señales de alarma en plantillas “creativas”',
        },
        {
          type: 'p',
          text: 'Antes: plantilla con sidebar oscura, iconos de teléfono en vez de la palabra “Email”, skill bars al 80%, y fechas en una columna separada del título. El ATS mezcla campos o pierde contacto. Después: encabezado simple con email en texto, experiencia en bloque cronológico, skills en lista separada por comas o viñetas.',
        },
        {
          type: 'ul',
          items: [
            'Tablas anidadas para simular layout de revista.',
            'Texto dentro de imágenes o formas.',
            'Encabezados inventados (“Mi viaje”, “Superpoderes”).',
            'Columnas que al exportar a PDF desordenan el orden de lectura.',
            'Fuentes decorativas que fallan al parsear o al imprimir.',
          ],
        },
        {
          type: 'h2',
          text: 'Una vs dos columnas: la verdad práctica',
        },
        {
          type: 'p',
          text: 'Las dos columnas no están prohibidas, pero son más frágiles. Algunos parsers leen de izquierda a derecha por filas y mezclan sidebar con experiencia. Si usas dos columnas, mantén contacto y skills en un lateral estable, y toda la experiencia narrativa en la columna principal, sin cruzar elementos.',
        },
        {
          type: 'p',
          text: 'Para máxima seguridad (portales agresivos, primera candidatura a una empresa grande), una sola columna gana. Puedes seguir teniendo un diseño limpio y moderno: tipografía clara, tipografía de títulos consistente, líneas sutiles y buen contraste.',
        },
        {
          type: 'h2',
          text: 'Tipografía, márgenes y longitud',
        },
        {
          type: 'p',
          text: 'Elige fuentes estándar (o muy cercanas) y tamaño legible: cuerpo 10–12 pt según, títulos diferenciados. Márgenes de al menos 1–1.5 cm. Evita comprimir todo a media página con letra 8 pt: el ATS quizá lea el texto, el humano no.',
        },
        {
          type: 'p',
          text: 'La plantilla no debe obligarte a relleno. Si eres junior, una página bien usada supera dos páginas vacías. Si tienes +10 años relevantes, dos páginas con plantilla aireada son preferibles a una página apretada e ilegible.',
        },
        {
          type: 'h2',
          text: 'Foto, color e iconos',
        },
        {
          type: 'p',
          text: 'En mercados donde la foto es habitual, la plantilla debe reservar un espacio que no rompa el parseo. Color de acento está bien si el texto sigue siendo negro/gris oscuro sobre fondo claro. Iconos de contacto son opcionales: nunca sustituyas el texto del email o teléfono solo con un pictograma.',
        },
        {
          type: 'p',
          text: 'Barras de idiomas o “soft skills al 70%” aportan poco y a veces confunden. Mejor: “Inglés C1”, “Francés B2”, o descripciones breves verificables. El ATS busca etiquetas, no porcentajes inventados.',
        },
        {
          type: 'h2',
          text: 'Checklist para evaluar cualquier plantilla en 5 minutos',
        },
        {
          type: 'ul',
          items: [
            'Abre el PDF y selecciona texto del resumen y de una viñeta de experiencia.',
            'Pega ese texto en un bloc de notas: ¿el orden tiene sentido?',
            'Busca email y teléfono: ¿aparecen como texto plano?',
            '¿Los títulos de sección son estándar o creativos?',
            '¿Hay tablas o cuadros de texto innecesarios?',
            'Exporta de nuevo: ¿el archivo pesa razonable y no es solo imagen?',
          ],
        },
        {
          type: 'h2',
          text: 'PDF o Word con la misma plantilla',
        },
        {
          type: 'p',
          text: 'Una buena plantilla debe exportar bien a ambos formatos cuando el portal lo pida. PDF fija el diseño; Word puede ser necesario si RR. HH. vuelca perfiles. En ambos casos, el contenido debe ser texto real. Si solo tienes un PNG bonito dentro de un PDF, no tienes plantilla ATS: tienes un cartel.',
        },
        {
          type: 'p',
          text: 'ClarCV ofrece plantillas pensadas para ATS y revisión humana: subes tu archivo, mejoras claridad con IA y generas una versión limpia. Empieza gratis; Pro a 7 €/mes si necesitas más iteraciones sin fricción.',
        },
        {
          type: 'h2',
          text: 'Errores al “personalizar” la plantilla',
        },
        {
          type: 'ul',
          items: [
            'Añadir una tercera columna “porque queda moderno”.',
            'Meter un gráfico de skills descargado de Internet.',
            'Renombrar Experiencia a un eslogan de marca personal.',
            'Poner el resumen en un cuadro de texto separado del flujo.',
            'Usar encabezados/pies con datos críticos que algunos parsers ignoran.',
          ],
        },
        {
          type: 'p',
          text: 'Personaliza color de acento, orden de secciones secundarias y densidad de viñetas. No personalices la arquitectura de lectura. La plantilla es un vehículo; tu contenido es el mensaje. Si el vehículo no llega al ATS, el mensaje no importa.',
        },
        {
          type: 'h2',
          text: 'Cómo testear la plantilla como lo haría un parser',
        },
        {
          type: 'p',
          text: 'Además de seleccionar texto, prueba pegar el contenido en un ATS de demo o en un conversor a texto plano. Observa si el email queda partido, si las fechas se desordenan o si las skills aparecen mezcladas con la dirección. Esos fallos predicen rechazos silenciosos en portales grandes.',
        },
        {
          type: 'p',
          text: 'Otra prueba: imprime a PDF desde Word/Google Docs y vuelve a abrir. Si el diseño “salta”, la plantilla es frágil. Las plantillas ClarCV están pensadas para mantener jerarquía tras exportar, precisamente para evitar ese efecto de “bonito en pantalla, roto al enviar”.',
        },
        {
          type: 'h2',
          text: 'Plantillas por nivel y sector',
        },
        {
          type: 'p',
          text: 'Un perfil creativo (diseño, marketing visual) a menudo quiere más personalidad. Aun así, el bloque de experiencia debe seguir siendo parseable. Reserva creatividad para color de acento, tipografía de títulos y portfolio enlazado; no para esconder el texto de logros dentro de ilustraciones.',
        },
        {
          type: 'p',
          text: 'En finanzas, legal o ingeniería, la sobriedad gana. Una columna, tipografía serena, cero iconografía innecesaria. En startups, puedes permitirte un acento de marca, pero el ATS corporativo del cliente final puede ser tan estricto como el de un banco. Diseña para el eslabón más débil del proceso.',
        },
        {
          type: 'h2',
          text: 'Migrar desde una plantilla mala sin reescribir todo',
        },
        {
          type: 'p',
          text: 'Copia primero el contenido a un documento plano (títulos + viñetas). Luego pégalo en una plantilla ATS limpia. No intentes “arreglar” una plantilla rota moviendo cuadros: perderás una tarde. ClarCV acelera este volcado: subes el archivo actual, recuperas el texto y aplicas una estructura compatible.',
        },
        {
          type: 'p',
          text: 'Al migrar, aprovecha para cortar relleno. Muchas plantillas creativas empujan a inventar secciones (“hobbies”, “soft skills al 90%”) que no aportan. Quédate con lo que prueba valor. El espacio recuperado mejora legibilidad humana tanto como el parseo.',
        },
        {
          type: 'p',
          text: 'Si necesitas foto (mercados ES/EU/LATAM), elige plantillas donde la imagen no desplace el email fuera del flujo de texto. Un retrato profesional no debería costarte el parseo del teléfono. Verifica siempre tras insertar la imagen que contacto y resumen siguen seleccionables.',
        },
        {
          type: 'h2',
          text: 'Exportación, peso del archivo y accesibilidad',
        },
        {
          type: 'p',
          text: 'Un PDF de 8 MB suele indicar imágenes sin comprimir o un escaneo. Apunta a un archivo ligero con tipografías embebidas. Si el portal tiene límite de tamaño, un PDF hinchado falla antes del ATS. Comprueba también contraste: texto gris claro sobre blanco puede verse “elegante” y ser ilegible para humanos y para OCR.',
        },
        {
          type: 'p',
          text: 'La accesibilidad no es solo ética: ayuda al parseo. Usa estilos de título reales (Heading 1/2) en Word en lugar de agrandar la fuente a mano. Etiqueta enlaces con URL visibles o texto descriptivo. Evita información crítica solo en encabezados/pies: algunos sistemas los ignoran.',
        },
        {
          type: 'p',
          text: 'Si trabajas con versión Word y PDF, genera ambas desde la misma fuente maestra. No edites una y olvides la otra. Un checklist mental: ¿misma fecha de “última actualización”? ¿Mismas viñetas? ¿Mismo email? La plantilla ATS es el sistema; la disciplina de versiones es el proceso.',
        },
        {
          type: 'ul',
          items: [
            'Comprueba el PDF en Adobe/Preview y en el visor del navegador.',
            'Abre el DOCX en otro ordenador si puedes: detecta fuentes faltantes.',
            'Desactiva marcas de agua “Confidential” que rompen lectura.',
            'No bloquees copia de texto: perjudica a parsers y a reclutadores.',
          ],
        },
        {
          type: 'p',
          text: 'Recuerda: la plantilla es infraestructura. Tu diferenciación está en logros, keywords verdaderas y un resumen que encaje con la oferta. Una plantilla perfecta con contenido genérico sigue perdiendo. Combina ambas capas y vuelve a testear tras cada cambio grande de diseño.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Las plantillas de Canva son compatibles con ATS?',
              a: 'Algunas sí si exportas texto real y un layout simple. Muchas fallan por capas, columnas agresivas o texto en elementos gráficos. Siempre prueba seleccionando texto en el PDF.',
            },
            {
              q: '¿Una sola columna es obligatoria?',
              a: 'No, pero es la opción más segura. Dos columnas pueden funcionar si el orden de lectura se mantiene y no mezclas experiencia con sidebar.',
            },
            {
              q: '¿Puedo usar color en una plantilla ATS?',
              a: 'Sí, con moderación. Mantén el texto principal en alto contraste. El color no sustituye estructura clara.',
            },
            {
              q: '¿Qué pasa si el portal pide Word?',
              a: 'Usa una plantilla que conserve estilos de párrafo y títulos reales en DOCX, no un diseño convertido de forma frágil. Evita text boxes.',
            },
            {
              q: '¿ClarCV reemplaza mi plantilla favorita?',
              a: 'Te da plantillas listas para ATS y un flujo de mejora desde tu PDF/Word. Si tu plantilla actual falla el test de texto seleccionable, conviene cambiar.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'ATS-friendly CV templates: what to look for and avoid',
      description:
        'How to choose a résumé template that looks good to humans and parses cleanly in ATS filters, with a checklist and common pitfalls.',
      readMinutes: 15,
      sections: [
        {
          type: 'p',
          text: 'A beautiful template can sink your application if the ATS cannot understand the file. Parsers need real text, predictable sections, and a logical order. Recruiters need visual hierarchy, breathing room, and achievements they can scan. The right template serves both.',
        },
        {
          type: 'p',
          text: '“ATS-compatible” is not a magic Canva badge. It is a set of decisions: typography, columns, tables, icons, headings, and export settings. This guide helps you evaluate any template—including ClarCV’s—before you send hundreds of applications with the same structural flaw.',
        },
        {
          type: 'h2',
          text: 'What makes a template ATS-friendly',
        },
        {
          type: 'p',
          text: 'An ATS turns your file into fields: name, email, roles, dates, skills. If the design breaks that mapping, you lose keywords—or worse, the system thinks you have no experience. Safe templates use top-to-bottom reading flow, standard headings, and minimal structural ornament.',
        },
        {
          type: 'ul',
          items: [
            'Selectable text (you can copy a sentence from the PDF).',
            'Clear section names: Experience, Education, Skills, Languages.',
            'Dates next to the role, easy to locate.',
            'One main content column (or two very stable columns).',
            'No floating text boxes, WordArt, or skill-bar charts.',
          ],
        },
        {
          type: 'h2',
          text: 'Red flags in “creative” templates',
        },
        {
          type: 'p',
          text: 'Before: dark sidebar, phone icons instead of the word “Email,” skill bars at 80%, and dates in a column separate from the title. The ATS mixes fields or drops contact info. After: a simple header with plain-text email, chronological experience blocks, and skills as a comma list or bullets.',
        },
        {
          type: 'ul',
          items: [
            'Nested tables that fake a magazine layout.',
            'Text inside images or shapes.',
            'Invented headings (“My journey,” “Superpowers”).',
            'Columns that scramble reading order when exported to PDF.',
            'Decorative fonts that fail parsing or printing.',
          ],
        },
        {
          type: 'h2',
          text: 'One vs two columns: the practical truth',
        },
        {
          type: 'p',
          text: 'Two columns are not forbidden, but they are more fragile. Some parsers read left to right by row and mix the sidebar with experience. If you use two columns, keep contact and skills in a stable side rail, and all narrative experience in the main column without crossing elements.',
        },
        {
          type: 'p',
          text: 'For maximum safety (aggressive portals, first application to a large company), a single column wins. You can still look clean and modern: clear type, consistent heading styles, subtle rules, and strong contrast.',
        },
        {
          type: 'h2',
          text: 'Typography, margins, and length',
        },
        {
          type: 'p',
          text: 'Choose standard (or near-standard) fonts and readable sizes: roughly 10–12 pt body, differentiated headings. Margins of at least 1–1.5 cm. Avoid cramming everything onto half a page with 8 pt type: the ATS may read it; a human will not.',
        },
        {
          type: 'p',
          text: 'A template should not force filler. If you are junior, one well-used page beats two empty ones. If you have 10+ relevant years, two pages with a breathable template beat one cramped, unreadable page.',
        },
        {
          type: 'h2',
          text: 'Photos, color, and icons',
        },
        {
          type: 'p',
          text: 'In markets where photos are common, the template should reserve space without breaking parsing. Accent color is fine if body text stays dark on a light background. Contact icons are optional: never replace the email or phone text with a pictogram alone.',
        },
        {
          type: 'p',
          text: 'Language bars or “soft skills at 70%” add little and sometimes confuse parsers. Prefer “English C1,” “French B2,” or short verifiable notes. ATS systems look for labels, not invented percentages.',
        },
        {
          type: 'h2',
          text: 'Checklist: evaluate any template in 5 minutes',
        },
        {
          type: 'ul',
          items: [
            'Open the PDF and select text from the summary and an experience bullet.',
            'Paste into a plain text editor: does the order still make sense?',
            'Find email and phone: are they plain text?',
            'Are section titles standard or overly creative?',
            'Are there unnecessary tables or text boxes?',
            'Re-export: is the file a reasonable size and not image-only?',
          ],
        },
        {
          type: 'h2',
          text: 'PDF or Word with the same template',
        },
        {
          type: 'p',
          text: 'A good template should export cleanly to both formats when portals require it. PDF locks layout; Word may be needed if HR dumps profiles into internal systems. In both cases, content must be real text. If you only have a pretty PNG inside a PDF, you do not have an ATS template—you have a poster.',
        },
        {
          type: 'p',
          text: 'ClarCV provides templates designed for ATS and human review: upload your file, improve clarity with AI, and generate a clean version. Start free; Pro is €7/month if you need more iterations without friction.',
        },
        {
          type: 'h2',
          text: 'Mistakes when “customizing” a template',
        },
        {
          type: 'ul',
          items: [
            'Adding a third column “because it looks modern.”',
            'Inserting a skill chart downloaded from the internet.',
            'Renaming Experience to a personal-brand slogan.',
            'Putting the summary in a text box outside the main flow.',
            'Placing critical data only in headers/footers some parsers ignore.',
          ],
        },
        {
          type: 'p',
          text: 'Customize accent color, secondary section order, and bullet density. Do not customize reading architecture. The template is the vehicle; your content is the message. If the vehicle never reaches the ATS, the message does not matter.',
        },
        {
          type: 'h2',
          text: 'How to test a template the way a parser would',
        },
        {
          type: 'p',
          text: 'Beyond selecting text, paste the content into a demo ATS or a plain-text converter. Watch for split emails, scrambled dates, or skills mixed into the address block. Those failures predict silent rejections on large portals.',
        },
        {
          type: 'p',
          text: 'Another test: print to PDF from Word/Google Docs and reopen. If the layout “jumps,” the template is fragile. ClarCV templates are designed to keep hierarchy after export—precisely to avoid “pretty on screen, broken when sent.”',
        },
        {
          type: 'h2',
          text: 'Templates by level and sector',
        },
        {
          type: 'p',
          text: 'Creative profiles (design, visual marketing) often want more personality. Even then, the experience block must stay parsable. Save creativity for accent color, heading type, and a linked portfolio—not for hiding achievement text inside illustrations.',
        },
        {
          type: 'p',
          text: 'In finance, legal, or engineering, sobriety wins. One column, calm typography, zero unnecessary iconography. In startups you can use a brand accent, but the end client’s corporate ATS may be as strict as a bank’s. Design for the weakest link in the process.',
        },
        {
          type: 'h2',
          text: 'Migrating from a bad template without rewriting everything',
        },
        {
          type: 'p',
          text: 'First copy content into a plain document (headings + bullets). Then paste into a clean ATS template. Do not spend an afternoon “fixing” a broken template by dragging boxes. ClarCV speeds this dump: upload the current file, recover the text, and apply a compatible structure.',
        },
        {
          type: 'p',
          text: 'While migrating, cut filler. Many creative templates push you to invent sections (“hobbies,” “soft skills at 90%”) that add little. Keep what proves value. Recovered space improves human readability as much as parsing.',
        },
        {
          type: 'p',
          text: 'If you need a photo (ES/EU/LATAM markets), choose templates where the image does not push email out of the text flow. A professional headshot should not cost you phone parsing. After inserting the image, always verify that contact and summary remain selectable.',
        },
        {
          type: 'h2',
          text: 'Export settings, file weight, and accessibility',
        },
        {
          type: 'p',
          text: 'An 8 MB PDF usually means uncompressed images or a scan. Aim for a light file with embedded fonts. If the portal has a size limit, a bloated PDF fails before the ATS. Also check contrast: light-gray text on white can look “elegant” and still be unreadable for humans and OCR.',
        },
        {
          type: 'p',
          text: 'Accessibility is not only ethics—it helps parsing. Use real heading styles (Heading 1/2) in Word instead of manually enlarging font. Label links with visible URLs or descriptive text. Avoid putting critical information only in headers/footers: some systems ignore them.',
        },
        {
          type: 'p',
          text: 'If you keep Word and PDF versions, generate both from the same master source. Do not edit one and forget the other. Mental checklist: same “last updated” date? Same bullets? Same email? The ATS template is the system; version discipline is the process.',
        },
        {
          type: 'ul',
          items: [
            'Check the PDF in Adobe/Preview and in a browser viewer.',
            'Open the DOCX on another computer if you can: missing fonts show up.',
            'Disable “Confidential” watermarks that break reading order.',
            'Do not block text copy: it hurts parsers and recruiters.',
          ],
        },
        {
          type: 'p',
          text: 'When in doubt, choose the simpler ClarCV layout over a decorative marketplace template. Simplicity is not a lack of ambition—it is how your achievements survive contact with software.',
        },
        {
          type: 'p',
          text: 'Remember: the template is infrastructure. Your differentiation sits in achievements, true keywords, and a summary that fits the job. A perfect template with generic content still loses. Combine both layers and re-test after every major design change. That discipline is what “ATS-compatible” means in practice—not a marketing sticker on a download page.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Are Canva templates ATS-compatible?',
              a: 'Some are, if you export real text and a simple layout. Many fail due to layers, aggressive columns, or text in graphic elements. Always test by selecting text in the PDF.',
            },
            {
              q: 'Is a single column mandatory?',
              a: 'No, but it is the safest option. Two columns can work if reading order holds and experience does not mix with the sidebar.',
            },
            {
              q: 'Can I use color on an ATS template?',
              a: 'Yes, in moderation. Keep main text high-contrast. Color does not replace clear structure.',
            },
            {
              q: 'What if the portal asks for Word?',
              a: 'Use a template that keeps real paragraph and heading styles in DOCX, not a fragile converted design. Avoid text boxes.',
            },
            {
              q: 'Does ClarCV replace my favorite template?',
              a: 'It gives you ATS-ready templates and an improvement flow from your PDF/Word. If your current template fails the selectable-text test, switch.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'checklist-cv-antes-de-enviar',
    date: '2026-08-08',
    category: 'career',
    related: ['disponibilidad-inmediata-cv', 'adaptar-cv-a-una-oferta', 'enviar-cv-por-email'],
    es: {
      title: 'Checklist del CV antes de enviar: 40 puntos que sí importan',
      description:
        'Lista práctica para revisar formato, contenido, keywords y archivo antes de pulsar “enviar candidatura”, con ejemplos de fallos típicos.',
      readMinutes: 16,
      sections: [
        {
          type: 'p',
          text: 'Enviar el CV “porque ya está” es la forma más cara de ahorrar diez minutos. Un typo en el email, un PDF-imagen o un resumen genérico pueden tumbar una candidatura buena. Esta checklist está pensada para usarla en voz alta, en orden, la noche antes o justo antes de aplicar.',
        },
        {
          type: 'p',
          text: 'No es una lista de vanidad. Cada punto responde a algo que ATS o humanos miran de verdad: parseo, relevancia, credibilidad y facilidad de contacto. Márcala completa una vez; después úsala en modo rápido (5–8 minutos) cuando adaptes el CV a cada oferta.',
        },
        {
          type: 'h2',
          text: '1. Datos de contacto y cabecera',
        },
        {
          type: 'ul',
          items: [
            'Nombre completo correcto y consistente con LinkedIn.',
            'Email profesional (evita apodos de adolescencia).',
            'Teléfono con prefijo internacional si aplicas fuera.',
            'Ciudad/país o “Remoto” según la oferta.',
            'URL de LinkedIn personalizada y funcional.',
            'Portfolio/GitHub solo si está actualizado y es relevante.',
          ],
        },
        {
          type: 'p',
          text: 'Antes: “xXdarkwolfXx@mail.com” y LinkedIn genérico con números. Después: nombre.apellido@… y linkedin.com/in/nombre-claro. Pequeño cambio, gran señal de cuidado.',
        },
        {
          type: 'h2',
          text: '2. Resumen y encaje con la oferta',
        },
        {
          type: 'p',
          text: 'Lee la oferta y subraya 5–8 requisitos reales. Tu resumen de 3–4 líneas debe reflejar rol, contexto y 1–2 fortalezas alineadas. Si el anuncio pide “atención al cliente B2B” y tu resumen habla solo de “pasión por aprender”, no hay encaje visible.',
        },
        {
          type: 'ul',
          items: [
            'El resumen menciona el tipo de rol al que aplicas.',
            'Incluye keywords verdaderas de la oferta (no inventadas).',
            'No es un objetivo vago (“buscar oportunidades de crecimiento”).',
            'Cabe en pocas líneas: escaneable en 5 segundos.',
          ],
        },
        {
          type: 'h2',
          text: '3. Experiencia: logros, no tareas',
        },
        {
          type: 'p',
          text: 'Cada puesto reciente debería responder: qué hiciste, en qué contexto y qué cambió. “Responsable de redes” es débil. “Aumenté el engagement un 32% en 6 meses con calendario editorial y A/B de creatividades” es usable en entrevista.',
        },
        {
          type: 'ul',
          items: [
            'Orden cronológico inverso.',
            'Título, empresa y fechas mes/año coherentes.',
            '3–6 viñetas en roles recientes; menos en roles antiguos.',
            'Verbos de acción al inicio de viñeta.',
            'Al menos algunas métricas o alcance (equipo, clientes, regiones).',
            'Sin jerga interna incomprensible fuera de tu empresa.',
          ],
        },
        {
          type: 'h2',
          text: '4. Formación, certificaciones e idiomas',
        },
        {
          type: 'ul',
          items: [
            'Títulos con nombre oficial y centro reconocible.',
            'Certificaciones pedidas en la oferta visibles si las tienes.',
            'Idiomas con nivel (B2, C1) o descripción honesta.',
            'Cursos irrelevantes eliminados o agrupados.',
            'Fechas de formación sin contradecir la experiencia.',
          ],
        },
        {
          type: 'h2',
          text: '5. Skills: calidad sobre lista infinita',
        },
        {
          type: 'p',
          text: 'Una lista de 40 skills diluye las 8 que importan. Prioriza herramientas y competencias del anuncio que realmente usas. Separa soft skills solo si aportan y puedes probarlas en logros (liderazgo, negociación, mentoring).',
        },
        {
          type: 'ul',
          items: [
            'Skills técnicas alineadas a la oferta en la parte alta.',
            'Sin duplicar lo mismo con sinónimos vacíos.',
            'Sin barras de porcentaje inventadas.',
            'Ortografía exacta de productos (Salesforce, not “sales force”).',
          ],
        },
        {
          type: 'h2',
          text: '6. Formato y ATS',
        },
        {
          type: 'ul',
          items: [
            'Texto seleccionable en el PDF.',
            'Secciones con nombres estándar.',
            'Sin tablas frágiles ni cuadros de texto rotos.',
            'Márgenes y tipografía legibles.',
            'Una o dos páginas según experiencia real.',
            'Foto solo si el mercado lo espera y no rompe el layout.',
          ],
        },
        {
          type: 'p',
          text: 'Prueba rápida: Ctrl+A en el PDF, copiar, pegar en un bloc de notas. Si el orden es un caos, el ATS también sufrirá. En ese caso, regenera con una plantilla ClarCV ATS-friendly antes de enviar.',
        },
        {
          type: 'h2',
          text: '7. Archivo, nombre y canal de envío',
        },
        {
          type: 'ul',
          items: [
            'Nombre de archivo: Nombre_Apellido_CV_Puesto.pdf (o similar limpio).',
            'Formato pedido por el portal (PDF o Word).',
            'Peso razonable; evita escaneos pesados.',
            'Versión correcta (no envíes el borrador_v3_final_FINAL).',
            'Si hay formulario online, pega campos con el mismo texto del CV.',
          ],
        },
        {
          type: 'h2',
          text: '8. Revisión humana final',
        },
        {
          type: 'ul',
          items: [
            'Ortografía y tildes (especialmente nombres propios).',
            'Coherencia de tiempos verbales.',
            'Números y porcentajes revisados.',
            'Enlaces clicables o URLs correctas.',
            'Impresión mental: ¿contratarías a esta persona en 20 segundos?',
          ],
        },
        {
          type: 'p',
          text: 'Si puedes, pide a una persona de confianza que marque solo lo confuso. No pidas “opinión general”: pide “qué no entiendes”. ClarCV ayuda en la pasada de claridad y estructura; la checklist humana evita el último error tonto.',
        },
        {
          type: 'h2',
          text: 'Modo rápido por candidatura (5 minutos)',
        },
        {
          type: 'p',
          text: 'Cuando ya tienes una base sólida, no rehagas todo. Ajusta resumen, reordena viñetas para keywords de la oferta, confirma skills críticas y exporta con nombre de archivo nuevo. Esa adaptación breve suele superar a un CV genérico perfecto enviando a ciegas.',
        },
        {
          type: 'h2',
          text: '9. Coherencia con la carta o el formulario',
        },
        {
          type: 'p',
          text: 'Si adjuntas carta de presentación o rellenas campos “Describe tu experiencia”, reutiliza las mismas cifras y títulos. Un 25% en el CV y un 40% en el formulario es una bandera roja. Copia-pega consciente desde la versión final del CV, no desde un borrador antiguo.',
        },
        {
          type: 'p',
          text: 'En portales con campos separados (empresa, puesto, fechas), escribe exactamente lo mismo que en el PDF. Algunos ATS cruzan ambos; las discrepancias bajan score o generan trabajo manual al recruiter, que rara vez juega a tu favor.',
        },
        {
          type: 'h2',
          text: '10. Prueba de escaneo de 20 segundos',
        },
        {
          type: 'p',
          text: 'Entrega el CV a alguien y pide: “En 20 segundos, ¿a qué puesto aplico y cuál es mi mayor prueba de valor?” Si no saben responder, el documento falla la prueba humana aunque pase el ATS. Reordena: resumen más nítido, primera viñeta de cada rol más fuerte, skills críticas más visibles.',
        },
        {
          type: 'p',
          text: 'Haz la misma prueba en móvil o con zoom: muchos reclutadores abren adjuntos en el teléfono. Si el encabezado ocupa media pantalla y el primer logro está abajo del pliegue, pierdes impacto. Las plantillas ClarCV priorizan jerarquía legible en esa primera mirada.',
        },
        {
          type: 'h2',
          text: 'Errores de última hora más comunes',
        },
        {
          type: 'ul',
          items: [
            'Enviar el CV con comentarios de revisión o modo control de cambios visible.',
            'Dejar “lorem ipsum” o un placeholder de teléfono.',
            'Adjuntar la versión con foto cuando el portal US pide sin foto (o al revés).',
            'Olvidar adaptar el nombre de archivo al puesto (parece envío masivo).',
            'Subir un Word bloqueado por contraseña o un PDF escaneado ilegible.',
          ],
        },
        {
          type: 'p',
          text: 'Monta un ritual: checklist completa al crear la base; modo rápido + prueba de 20 segundos en cada envío importante. Ese hábito evita el “lo envié mal” que no se puede deshacer. Guarda la versión final en una carpeta por proceso (“Empresa_Puesto_fecha”) para no perder el hilo si te llaman semanas después.',
        },
        {
          type: 'p',
          text: 'Si usas IA para pulir redacción, vuelve a leer en voz alta nombres de empresas, cifras y tecnologías. Los modelos pueden “suavizar” un número o renombrar una herramienta. La checklist humana al final sigue siendo obligatoria: ClarCV acelera estructura; tú firmas la verdad.',
        },
        {
          type: 'h2',
          text: 'Checklist específica por canal de envío',
        },
        {
          type: 'p',
          text: 'Email directo a una persona: PDF, asunto claro con puesto, cuerpo breve que repite 1 logro del CV, firma con teléfono. Portal ATS: respeta el formato pedido, completa todos los campos obligatorios con el mismo texto, no dejes “ver CV adjunto” en campos vacíos. LinkedIn Easy Apply: asegúrate de que el archivo subido es la versión adaptada, no una antigua guardada en la plataforma.',
        },
        {
          type: 'p',
          text: 'Agencias de selección: a veces prefieren Word para reeditar. Pregunta si puedes. Si envías Word, elimina comentarios y propiedades del documento que revelen el nombre de un empleador anterior o un path interno. Limpia metadatos básicos.',
        },
        {
          type: 'ul',
          items: [
            '¿El asunto/nombre de archivo incluye el puesto exacto?',
            '¿Adjunté la versión correcta (ES/EN, con/sin foto)?',
            '¿El cuerpo del email no contradice el resumen?',
            '¿Confirmé el email de destino (sin typos)?',
            '¿Guardé copia de lo enviado y la fecha?',
          ],
        },
        {
          type: 'p',
          text: 'La checklist no es burocracia: es un sistema anti-error. Los candidatos que la usan envían menos, pero mejor. En procesos competitivos, “mejor” gana a “más”. ClarCV acorta la parte de formato; esta lista protege el último kilometro hasta el botón de enviar.',
        },
        {
          type: 'p',
          text: 'Último filtro emocional: ¿enviarías este PDF a alguien a quien respetas en tu sector? Si la respuesta es dudosa, no pulses enviar. Ajusta una viñeta débil, corrige un typo o regenera la plantilla. Diez minutos más hoy evitan una semana de silencio innecesario mañana.',
        },
        {
          type: 'p',
          text: 'Cuando tengas la base estable, imprime esta checklist o guárdala junto a ClarCV en tus favoritos. La repetición convierte el cuidado en reflejo. Los procesos de selección no premian la prisa: premian la claridad sostenida.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Debo usar la checklist completa en cada envío?',
              a: 'La completa al crear o rehacer el CV. Luego usa el modo rápido de adaptación por oferta: resumen, keywords, skills y archivo.',
            },
            {
              q: '¿Qué es lo más crítico si solo tengo 2 minutos?',
              a: 'Contacto correcto, texto seleccionable, resumen alineado a la oferta y nombre de archivo limpio.',
            },
            {
              q: '¿PDF o Word en la checklist?',
              a: 'El que pida el anuncio o portal. Ambos deben ser texto real. Si no especifican, PDF suele ser seguro para preservar formato.',
            },
            {
              q: '¿Cuántas páginas son aceptables?',
              a: 'Junior: suele bastar 1. Perfiles con trayectoria densa: 2 si aportan. Lo ilegible por comprimir es peor que una segunda página clara.',
            },
            {
              q: '¿La IA sustituye esta checklist?',
              a: 'No. ClarCV acelera claridad y plantilla ATS; tú validas verdad, encaje con la oferta y el último control humano.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'CV checklist before you apply: 40 points that actually matter',
      description:
        'A practical list to review format, content, keywords, and file settings before you hit submit—with examples of common failures.',
      readMinutes: 16,
      sections: [
        {
          type: 'p',
          text: 'Sending a CV “because it is done” is an expensive way to save ten minutes. A typo in the email, an image-only PDF, or a generic summary can kill a strong application. Use this checklist out loud, in order, the night before—or right before you apply.',
        },
        {
          type: 'p',
          text: 'This is not a vanity list. Each item maps to something ATS systems or humans actually check: parsing, relevance, credibility, and easy contact. Complete it fully once; then use a fast mode (5–8 minutes) when you tailor the CV to each job ad.',
        },
        {
          type: 'h2',
          text: '1. Contact details and header',
        },
        {
          type: 'ul',
          items: [
            'Full name correct and consistent with LinkedIn.',
            'Professional email (avoid teenage nicknames).',
            'Phone with country code if you apply abroad.',
            'City/country or “Remote” as the ad expects.',
            'Custom LinkedIn URL that works.',
            'Portfolio/GitHub only if updated and relevant.',
          ],
        },
        {
          type: 'p',
          text: 'Before: “xXdarkwolfXx@mail.com” and a generic LinkedIn with numbers. After: first.last@… and linkedin.com/in/clear-name. Small change, strong signal of care.',
        },
        {
          type: 'h2',
          text: '2. Summary and fit with the job ad',
        },
        {
          type: 'p',
          text: 'Read the ad and underline 5–8 real requirements. Your 3–4 line summary should reflect role, context, and 1–2 aligned strengths. If the posting asks for “B2B customer success” and your summary only says you are “passionate about learning,” fit is invisible.',
        },
        {
          type: 'ul',
          items: [
            'The summary names the type of role you want.',
            'It includes true keywords from the ad (not invented ones).',
            'It is not a vague objective (“seeking growth opportunities”).',
            'It fits in a few lines: scannable in 5 seconds.',
          ],
        },
        {
          type: 'h2',
          text: '3. Experience: achievements, not duties',
        },
        {
          type: 'p',
          text: 'Each recent role should answer: what you did, in what context, and what changed. “Responsible for social media” is weak. “Increased engagement 32% in 6 months with an editorial calendar and creative A/B tests” is interview-ready.',
        },
        {
          type: 'ul',
          items: [
            'Reverse chronological order.',
            'Title, company, and month/year dates that match reality.',
            '3–6 bullets on recent roles; fewer on older ones.',
            'Action verbs at the start of bullets.',
            'Some metrics or scope (team, customers, regions).',
            'No internal jargon outsiders cannot decode.',
          ],
        },
        {
          type: 'h2',
          text: '4. Education, certifications, and languages',
        },
        {
          type: 'ul',
          items: [
            'Degrees with official names and recognizable schools.',
            'Certifications requested in the ad visible if you have them.',
            'Languages with level (B2, C1) or an honest description.',
            'Irrelevant courses removed or grouped.',
            'Education dates that do not contradict work history.',
          ],
        },
        {
          type: 'h2',
          text: '5. Skills: quality over an endless list',
        },
        {
          type: 'p',
          text: 'A list of 40 skills dilutes the 8 that matter. Prioritize tools and competencies from the ad that you truly use. Separate soft skills only if they add value and you can prove them in achievements (leadership, negotiation, mentoring).',
        },
        {
          type: 'ul',
          items: [
            'Technical skills aligned to the ad near the top.',
            'No empty synonym duplicates.',
            'No invented percentage bars.',
            'Exact product spelling (Salesforce, not “sales force”).',
          ],
        },
        {
          type: 'h2',
          text: '6. Format and ATS',
        },
        {
          type: 'ul',
          items: [
            'Selectable text in the PDF.',
            'Standard section names.',
            'No fragile tables or broken text boxes.',
            'Readable margins and typography.',
            'One or two pages based on real experience.',
            'Photo only if the market expects it and layout stays clean.',
          ],
        },
        {
          type: 'p',
          text: 'Quick test: select all in the PDF, copy, paste into a plain text editor. If the order is chaos, the ATS will struggle too. In that case, regenerate with an ATS-friendly ClarCV template before you apply.',
        },
        {
          type: 'h2',
          text: '7. File, naming, and submission channel',
        },
        {
          type: 'ul',
          items: [
            'File name: First_Last_CV_Role.pdf (or similarly clean).',
            'Format required by the portal (PDF or Word).',
            'Reasonable file size; avoid heavy scans.',
            'Correct version (not draft_v3_final_FINAL).',
            'If there is an online form, paste fields using the same CV wording.',
          ],
        },
        {
          type: 'h2',
          text: '8. Final human review',
        },
        {
          type: 'ul',
          items: [
            'Spelling and accents (especially proper nouns).',
            'Consistent verb tense.',
            'Numbers and percentages double-checked.',
            'Working links or correct URLs.',
            'Mental print test: would you interview this person in 20 seconds?',
          ],
        },
        {
          type: 'p',
          text: 'If you can, ask a trusted person to mark only what confuses them. Do not ask for “general opinion”—ask “what do you not understand?” ClarCV helps with clarity and structure; the human checklist catches the last silly mistake.',
        },
        {
          type: 'h2',
          text: 'Fast mode per application (5 minutes)',
        },
        {
          type: 'p',
          text: 'Once you have a solid base, do not rebuild everything. Tune the summary, reorder bullets for job-ad keywords, confirm critical skills, and export with a new file name. That short tailoring usually beats a perfect generic CV sent blindly.',
        },
        {
          type: 'h2',
          text: '9. Consistency with the cover letter or form',
        },
        {
          type: 'p',
          text: 'If you attach a cover letter or fill “Describe your experience” fields, reuse the same figures and titles. 25% on the CV and 40% on the form is a red flag. Consciously copy from the final CV version, not an old draft.',
        },
        {
          type: 'p',
          text: 'On portals with separate fields (company, title, dates), type exactly what the PDF says. Some ATS tools cross-check both; mismatches lower score or create manual work for recruiters, which rarely helps you.',
        },
        {
          type: 'h2',
          text: '10. The 20-second scan test',
        },
        {
          type: 'p',
          text: 'Hand the CV to someone and ask: “In 20 seconds, what role am I applying for and what is my strongest proof of value?” If they cannot answer, the document fails the human test even if it passes ATS. Reorder: sharper summary, stronger first bullet per role, critical skills more visible.',
        },
        {
          type: 'p',
          text: 'Run the same test on mobile or with zoom: many recruiters open attachments on a phone. If the header fills half the screen and the first achievement is below the fold, you lose impact. ClarCV templates prioritize readable hierarchy for that first glance.',
        },
        {
          type: 'h2',
          text: 'Most common last-minute mistakes',
        },
        {
          type: 'ul',
          items: [
            'Sending a CV with review comments or track-changes still visible.',
            'Leaving “lorem ipsum” or a phone placeholder.',
            'Attaching the photo version when a US portal wants no photo (or the reverse).',
            'Forgetting to rename the file for the role (looks like mass apply).',
            'Uploading a password-locked Word file or an unreadable scanned PDF.',
          ],
        },
        {
          type: 'p',
          text: 'Build a ritual: full checklist when you create the base; fast mode + 20-second test on every important submission. That habit prevents the irreversible “I sent the wrong one.” Store the final version in a per-process folder (“Company_Role_date”) so you still know what you sent if they call weeks later.',
        },
        {
          type: 'p',
          text: 'If you use AI to polish wording, re-read company names, figures, and technologies out loud. Models can “smooth” a number or rename a tool. The final human checklist remains mandatory: ClarCV speeds structure; you sign off on truth.',
        },
        {
          type: 'h2',
          text: 'Channel-specific checklist',
        },
        {
          type: 'p',
          text: 'Direct email to a person: PDF, clear subject with the role, short body repeating one CV win, signature with phone. ATS portal: respect the required format, fill every mandatory field with the same wording, do not leave “see attached CV” in empty fields. LinkedIn Easy Apply: make sure the uploaded file is the tailored version, not an old one stored on the platform.',
        },
        {
          type: 'p',
          text: 'Recruiting agencies sometimes prefer Word so they can re-edit. Ask if you may. If you send Word, remove comments and document properties that reveal a previous employer name or an internal path. Clean basic metadata.',
        },
        {
          type: 'ul',
          items: [
            'Does the subject/file name include the exact role?',
            'Did I attach the correct version (ES/EN, with/without photo)?',
            'Does the email body contradict the summary?',
            'Did I confirm the destination email (no typos)?',
            'Did I save a copy of what I sent and the date?',
          ],
        },
        {
          type: 'p',
          text: 'The checklist is not bureaucracy—it is an anti-error system. Candidates who use it send fewer applications, but better ones. In competitive processes, “better” beats “more.” ClarCV shortens the formatting work; this list protects the last mile to the submit button.',
        },
        {
          type: 'p',
          text: 'Print the fast-mode list near your desk or keep it as a pinned note. Habits beat memory when you are tired at 11 p.m. applying after work. Tired brains skip contact typos; checklists do not.',
        },
        {
          type: 'p',
          text: 'Final emotional filter: would you send this PDF to someone you respect in your field? If the answer is shaky, do not hit submit. Fix one weak bullet, correct a typo, or regenerate the template. Ten more minutes today can prevent a week of unnecessary silence tomorrow.',
        },
        {
          type: 'p',
          text: 'Once your base is stable, print this checklist or keep it next to ClarCV in your bookmarks. Repetition turns care into reflex. Hiring processes do not reward haste—they reward sustained clarity across every file you send.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I run the full checklist on every application?',
              a: 'Use the full list when you create or rebuild the CV. Then use fast adaptation mode per job: summary, keywords, skills, and file.',
            },
            {
              q: 'What matters most if I only have 2 minutes?',
              a: 'Correct contact info, selectable text, a summary aligned to the ad, and a clean file name.',
            },
            {
              q: 'PDF or Word on the checklist?',
              a: 'Whatever the ad or portal asks for. Both must be real text. If unspecified, PDF is usually safe for preserving layout.',
            },
            {
              q: 'How many pages are acceptable?',
              a: 'Junior: one often suffices. Dense careers: two if they add value. Unreadable compression is worse than a clear second page.',
            },
            {
              q: 'Does AI replace this checklist?',
              a: 'No. ClarCV speeds clarity and ATS templates; you still validate truth, job fit, and the final human check.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-para-tecnologia',
    date: '2026-08-08',
    category: 'career',
    related: ['cv-para-data-analyst', 'palabras-clave-cv-ats', 'proyectos-personales-github-cv'],
    es: {
      title: 'CV para developers, data y producto: stack real',
      description:
        'Cómo hacer un CV tech para developers, data y producto: stack honesto, logros con alcance y PDF que pase ATS. Qué mira el hiring manager. ClarCV.',
      readMinutes: 15,
      sections: [
        {
          type: 'p',
          text: 'En tecnología el CV compite en dos frentes: el ATS busca keywords de stack y el entrevistador técnico busca evidencia. Un listado infinito de lenguajes sin contexto no basta. Tampoco un diseño creativo que el parser no lee. Necesitas claridad, pruebas y priorización.',
        },
        {
          type: 'p',
          text: 'Esta guía cubre perfiles de software, data, QA, DevOps y producto digital. Los principios son comunes: logros con alcance, stack honesto, proyectos seleccionados y un resumen orientado al rol. Adapta el énfasis: un backend senior no se vende igual que un data analyst junior.',
        },
        {
          type: 'p',
          text: 'El parseo ATS y el formato del PDF están en [cómo hacer un CV para filtros ATS](/blog/cv-para-filtros-ats). El perfil analista (SQL, BI, impacto) está en [CV de data analyst](/blog/cv-para-data-analyst). Aquí el foco es stack, logros y cómo te lee un hiring manager técnico.',
        },
        { type: 'cta' },
        {
          type: 'h2',
          text: 'Estructura que funciona en tech',
        },
        {
          type: 'p',
          text: 'Orden recomendado: contacto (con GitHub/portfolio si aporta), resumen, skills técnicas clave, experiencia, proyectos destacados (si aportan), formación y certificaciones. Los idiomas y soft skills van al final salvo que la oferta los marque como críticos.',
        },
        {
          type: 'ul',
          items: [
            'Resumen: rol + años/contexto + dominio (backend, data, mobile…) + 1 impacto.',
            'Skills: agrupadas (lenguajes, cloud, datos, herramientas) sin inventar nivel falso.',
            'Experiencia: viñetas con problema → acción → resultado.',
            'Proyectos: 2–4 máximo, con stack y outcome, no tutoriales genéricos.',
          ],
        },
        {
          type: 'h2',
          text: 'Skills: cómo listar el stack sin parecer spam',
        },
        {
          type: 'p',
          text: 'Antes: “Python, Java, C++, Go, Rust, Haskell, COBOL, Excel, Word, PowerPoint…” Después: “Python, FastAPI, PostgreSQL, Docker, AWS (ECS, S3), pytest” — las herramientas que usaste de verdad en los últimos roles o proyectos fuertes. El resto puede vivir en LinkedIn o mencionarse si la oferta lo pide y tienes base real.',
        },
        {
          type: 'p',
          text: 'Ortografía exacta importa para ATS: “PostgreSQL”, “Kubernetes”, “TypeScript”. Si la oferta dice “React” y tú escribes solo “Frontend moderno”, puedes perder el match aunque sepas React. Sé específico y veraz.',
        },
        {
          type: 'h2',
          text: 'Experiencia: de tickets a impacto',
        },
        {
          type: 'p',
          text: 'Los hiring managers tech odian las viñetas de “participé en el desarrollo de…”. Prefieren ownership y resultado. Incluye escala cuando puedas: usuarios, requests, tamaño de dataset, frecuencia de deploys, reducción de incidentes, tiempo de build, conversion, ahorro de coste.',
        },
        {
          type: 'p',
          text: 'Ejemplo débil: “Trabajé en microservicios con Java.” Ejemplo fuerte: “Diseñé e implementé un servicio de pagos en Java/Spring que procesó 120k transacciones/mes y redujo errores de conciliación un 40%.” Misma persona, distinta señal.',
        },
        {
          type: 'ul',
          items: [
            'Nombra tecnologías dentro de la viñeta cuando aporten contexto.',
            'Señala colaboración (con producto, data, soporte) si el rol es híbrido.',
            'Menciona prácticas: CI/CD, code review, on-call, testing, documentación.',
            'Evita secretos de empresa; habla de impacto sin datos confidenciales.',
          ],
        },
        {
          type: 'h2',
          text: 'Proyectos y GitHub: cuándo ayudan de verdad',
        },
        {
          type: 'p',
          text: 'Un GitHub vacío o lleno de forks sin README perjudica. Mejor 2–3 repos con descripción clara, README, y algo que demuestre criterio (tests, estructura, deploy). En el CV, una línea: nombre del proyecto, problema, stack, resultado o link.',
        },
        {
          type: 'p',
          text: 'Si trabajas en código cerrado, los proyectos side no son obligatorios, pero un caso de estudio redactado (arquitectura, trade-offs) puede sustituir. Para data: notebooks limpios o dashboards públicos anonimizados. Para producto: case studies de discovery e impacto.',
        },
        {
          type: 'h2',
          text: 'CV por especialidad: matices rápidos',
        },
        {
          type: 'ul',
          items: [
            'Frontend: accesibilidad, performance, design system, testing de UI.',
            'Backend: APIs, datos, fiabilidad, observabilidad, coste.',
            'Data: fuentes, modelos, métricas de negocio, calidad de datos.',
            'DevOps/SRE: uptime, automatización, IaC, incident response.',
            'Producto: outcomes, research, priorización, stakeholders, métricas North Star.',
          ],
        },
        {
          type: 'h2',
          text: 'ATS y portales tech',
        },
        {
          type: 'p',
          text: 'Muchas empresas tech usan Greenhouse, Lever, Workday u otros ATS. Sigue valiendo texto seleccionable y secciones claras. Adapta el resumen y el bloque de skills a cada oferta sin mentir. Si piden “3 años de Kubernetes” y tienes uno, no inventes: muestra aprendizaje + proyectos + disposición a demostrar en prueba técnica.',
        },
        {
          type: 'p',
          text: 'ClarCV te ayuda a partir de tu PDF/Word actual: clarifica logros, aplica plantilla ATS y genera una versión limpia para iterar por oferta. En tech, iterar rápido importa tanto como el primer draft.',
        },
        {
          type: 'h2',
          text: 'Errores típicos en CVs tecnológicos',
        },
        {
          type: 'ul',
          items: [
            'Lista de moda (todas las buzzwords del año) sin evidencia.',
            'Ocultar el stack solo en un gráfico visual.',
            'Puestos sin fechas o con títulos ambiguos (“Ninja”, “Guru”).',
            'Proyectos académicos eternos sin resultado.',
            'CV de 4 páginas con cada ticket de Jira.',
          ],
        },
        {
          type: 'p',
          text: 'Prioriza. Un CV tech excelente parece simple: pocas secciones, mucha prueba. Si un bullet no te ayuda a contar una historia de entrevista, córtalo.',
        },
        {
          type: 'h2',
          text: 'Pruebas técnicas, take-homes y cómo reflejarlos',
        },
        {
          type: 'p',
          text: 'Si completaste un take-home notable o mantienes un kata bien documentado, una línea bajo proyectos ayuda: “Take-home de pipelines ETL (Python/Airflow): procesamiento de 2M filas con tests y README.” No hace falta adjuntar el código en el CV; el enlace o la mención prepara la conversación.',
        },
        {
          type: 'p',
          text: 'Evita narrar rechazos o “aprobé la prueba de X empresa”. Sí puedes describir el tipo de problema que sabes resolver. El CV vende capacidad; la prueba la demostrarás otra vez en el proceso actual.',
        },
        {
          type: 'h2',
          text: 'Seniority: cómo no parecer inflado ni infravalorado',
        },
        {
          type: 'p',
          text: 'Senior no es una lista más larga de acrónimos: es ownership, mentoría, decisiones de diseño y tipología de problemas. Un mid con buen impacto supera a un “senior” que solo listó frameworks. En viñetas, muestra decisiones (“elegí X frente a Y por latencia”) cuando sea cierto.',
        },
        {
          type: 'p',
          text: 'Si vienes de bootcamp o cambio a tech, no compitas inventando años. Compite con proyectos sólidos, contribuciones claras y un resumen que declare el nivel real (“Junior developer con background en…”). La honestidad acelera el match con vacantes adecuadas.',
        },
        {
          type: 'h2',
          text: 'CV bilingüe y remotos internacionales',
        },
        {
          type: 'p',
          text: 'Para roles remote-first, inglés suele ser el idioma del CV aunque vivas en España. Mantén una versión ES para empresas locales. Asegura que el stack y los logros sean idénticos en hechos. ClarCV te permite partir del mismo archivo y generar salidas claras en el idioma que pida cada oferta.',
        },
        {
          type: 'p',
          text: 'Incluye franja horaria o ciudad si es relevante para colaboración. “Madrid (CET) · remoto” evita malentendidos. Si tienes derecho a trabajar en ciertos países, una línea discreta en contacto puede desbloquear filtros de movilidad.',
        },
        {
          type: 'p',
          text: 'Cuando adaptes el CV a una oferta, no reordenes solo las skills: mueve arriba la viñeta de experiencia que mejor demuestre el requisito número uno del anuncio. El ATS cuenta keywords; el hiring manager cuenta evidencia. Necesitas ambas capas en la misma página.',
        },
        {
          type: 'h2',
          text: 'Métricas que funcionan (y cómo estimarlas con honestidad)',
        },
        {
          type: 'p',
          text: 'Si no tienes un dashboard perfecto, aún puedes cuantificar: número de servicios, frecuencia de release, tamaño del monolito migrado, tiempo de onboarding de nuevos devs, tickets P1 al mes, cobertura de tests, coste cloud antes/después. Redondea con honestidad y prepárate a explicar la fuente en entrevista.',
        },
        {
          type: 'p',
          text: 'Evita vanity metrics vacías (“escribí 50.000 líneas de código”). Prefiere outcome: menos incidentes, más velocidad de entrega, mejor DX, ahorro. Si el impacto fue de equipo, dilo: “junto con 3 ingenieros” no resta mérito; suma credibilidad.',
        },
        {
          type: 'ul',
          items: [
            'Latencia, error rate, uptime, throughput.',
            'Adoption interna de una herramienta o librería.',
            'Tiempo de build/CI y flaky tests eliminados.',
            'Migraciones completadas sin downtime.',
            'Mentoría: personas, duración, resultado (promoción, autonomía).',
          ],
        },
        {
          type: 'p',
          text: 'El CV tech excelente cabe en una mirada: stack correcto arriba, evidencia abajo. Si tras leerlo un staff engineer puede imaginar cómo trabajarías juntos, está listo. Si solo ve una sopa de logos, sigue editando. ClarCV ayuda a limpiar estructura; las métricas honestas son tu ventaja competitiva.',
        },
        {
          type: 'h2',
          text: 'Qué miran hiring managers técnicos en 60 segundos',
        },
        {
          type: 'p',
          text: 'Trayectoria reciente, stack alineado, señales de ownership y escritura clara. Si esas cuatro están, pasan a detalle. Si el CV parece un inventario de cursos sin impacto, saltan al siguiente. Por eso cada sección debe ganar su espacio: menos ruido, más prueba.',
        },
        {
          type: 'p',
          text: 'Incluye enlaces solo si cargan rápido y muestran trabajo actual. Un portfolio caído o un GitHub sin commits recientes hace más daño que no poner enlace. Mantén README en el repo principal y una demo mínima si el rol es frontend o full-stack.',
        },
        {
          type: 'p',
          text: 'Cierra el ciclo: adapta, exporta, aplica, anota feedback. El CV tech no es un monumento; es un documento de iteración continua. ClarCV acorta el ciclo de formato para que puedas invertir tiempo en evidencias que sí cambian el resultado. Revisa también que el PDF siga siendo texto seleccionable tras cada exportación.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Debo poner todas las tecnologías que he tocado?',
              a: 'No. Prioriza las relevantes y recientes. Menciona otras solo si la oferta las pide y tienes base real para defenderlas.',
            },
            {
              q: '¿Es obligatorio GitHub?',
              a: 'No, pero ayuda en roles hands-on si muestra trabajo real. Código cerrado + logros medibles también funciona.',
            },
            {
              q: '¿Una o dos páginas en tech?',
              a: 'Junior: 1. Mid/senior con impacto denso: 2 puede estar bien. Evita relleno de herramientas irrelevantes.',
            },
            {
              q: '¿Cómo demuestro soft skills en un CV tech?',
              a: 'Con viñetas: mentoring, liderazgo técnico, coordinación con producto, comunicación de incidentes. No con una lista vacía de adjetivos.',
            },
            {
              q: '¿Puedo usar la misma versión para todas las ofertas?',
              a: 'Base sí; skills y resumen deberían moverse según el anuncio. La adaptación corta mejora el match ATS y la primera lectura humana.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Developer, data, and product CV: real stack',
      description:
        'How to write a tech CV for developers, data, and product: honest stack, scoped impact, and an ATS-readable PDF. What hiring managers actually scan. ClarCV.',
      readMinutes: 15,
      sections: [
        {
          type: 'p',
          text: 'In tech, your CV competes on two fronts: the ATS looks for stack keywords, and the technical interviewer looks for evidence. An endless language list without context is not enough. Neither is a creative design the parser cannot read. You need clarity, proof, and prioritization.',
        },
        {
          type: 'p',
          text: 'This guide covers software, data, QA, DevOps, and digital product profiles. The principles are shared: scoped achievements, an honest stack, selected projects, and a role-focused summary. Adjust emphasis: a senior backend engineer does not sell the same way as a junior data analyst.',
        },
        {
          type: 'p',
          text: 'ATS parsing and PDF format are in [how to make a CV for ATS filters](/blog/cv-para-filtros-ats). The analyst profile (SQL, BI, impact) is in [data analyst CV](/blog/cv-para-data-analyst). This page is about stack, achievements, and how a technical hiring manager reads you.',
        },
        { type: 'cta' },
        {
          type: 'h2',
          text: 'A structure that works in tech',
        },
        {
          type: 'p',
          text: 'Recommended order: contact (with GitHub/portfolio if it adds value), summary, key technical skills, experience, featured projects (if useful), education, and certifications. Languages and soft skills go last unless the ad marks them as critical.',
        },
        {
          type: 'ul',
          items: [
            'Summary: role + years/context + domain (backend, data, mobile…) + one impact line.',
            'Skills: grouped (languages, cloud, data, tools) without fake proficiency claims.',
            'Experience: bullets with problem → action → result.',
            'Projects: 2–4 max, with stack and outcome—not generic tutorials.',
          ],
        },
        {
          type: 'h2',
          text: 'Skills: how to list your stack without looking like spam',
        },
        {
          type: 'p',
          text: 'Before: “Python, Java, C++, Go, Rust, Haskell, COBOL, Excel, Word, PowerPoint…” After: “Python, FastAPI, PostgreSQL, Docker, AWS (ECS, S3), pytest”—tools you actually used in recent roles or strong projects. Everything else can live on LinkedIn or appear only if the ad asks and you can defend it.',
        },
        {
          type: 'p',
          text: 'Exact spelling matters for ATS: “PostgreSQL,” “Kubernetes,” “TypeScript.” If the ad says “React” and you only write “modern frontend,” you may miss the match even if you know React. Be specific and truthful.',
        },
        {
          type: 'h2',
          text: 'Experience: from tickets to impact',
        },
        {
          type: 'p',
          text: 'Tech hiring managers dislike bullets that say “participated in the development of…” They prefer ownership and outcome. Include scale when you can: users, requests, dataset size, deploy frequency, incident reduction, build time, conversion, cost savings.',
        },
        {
          type: 'p',
          text: 'Weak: “Worked on microservices with Java.” Strong: “Designed and shipped a Java/Spring payments service that processed 120k transactions/month and cut reconciliation errors by 40%.” Same person, different signal.',
        },
        {
          type: 'ul',
          items: [
            'Name technologies inside bullets when they add context.',
            'Show collaboration (product, data, support) for hybrid roles.',
            'Mention practices: CI/CD, code review, on-call, testing, documentation.',
            'Avoid company secrets; describe impact without confidential figures.',
          ],
        },
        {
          type: 'h2',
          text: 'Projects and GitHub: when they truly help',
        },
        {
          type: 'p',
          text: 'An empty GitHub—or one full of forks with no README—hurts. Prefer 2–3 repos with a clear description, README, and signs of judgment (tests, structure, deploy). On the CV, one line: project name, problem, stack, outcome or link.',
        },
        {
          type: 'p',
          text: 'If you work in closed-source code, side projects are not mandatory, but a written case study (architecture, trade-offs) can substitute. For data: clean notebooks or anonymized public dashboards. For product: discovery and outcome case studies.',
        },
        {
          type: 'h2',
          text: 'CV by specialty: quick nuances',
        },
        {
          type: 'ul',
          items: [
            'Frontend: accessibility, performance, design systems, UI testing.',
            'Backend: APIs, data, reliability, observability, cost.',
            'Data: sources, models, business metrics, data quality.',
            'DevOps/SRE: uptime, automation, IaC, incident response.',
            'Product: outcomes, research, prioritization, stakeholders, North Star metrics.',
          ],
        },
        {
          type: 'h2',
          text: 'ATS and tech hiring portals',
        },
        {
          type: 'p',
          text: 'Many tech companies use Greenhouse, Lever, Workday, or similar ATS tools. Selectable text and clear sections still win. Tune summary and skills per ad without lying. If they ask for “3 years of Kubernetes” and you have one, do not invent it—show learning + projects + readiness for a technical assessment.',
        },
        {
          type: 'p',
          text: 'ClarCV helps from your current PDF/Word: clarify achievements, apply an ATS template, and generate a clean version you can iterate per job. In tech, iterating fast matters as much as the first draft.',
        },
        {
          type: 'h2',
          text: 'Common mistakes on tech CVs',
        },
        {
          type: 'ul',
          items: [
            'Trend lists (every buzzword of the year) with no evidence.',
            'Hiding the stack only inside a visual chart.',
            'Roles without dates or with vague titles (“Ninja,” “Guru”).',
            'Endless academic projects with no outcome.',
            'A 4-page CV with every Jira ticket.',
          ],
        },
        {
          type: 'p',
          text: 'Prioritize. A great tech CV looks simple: few sections, lots of proof. If a bullet does not help you tell an interview story, cut it.',
        },
        {
          type: 'h2',
          text: 'Technical assessments, take-homes, and how to reflect them',
        },
        {
          type: 'p',
          text: 'If you completed a strong take-home or keep a well-documented kata, one line under projects helps: “ETL pipeline take-home (Python/Airflow): processed 2M rows with tests and README.” You do not need to attach code on the CV; the link or mention prepares the conversation.',
        },
        {
          type: 'p',
          text: 'Avoid narrating rejections or “I passed company X’s test.” You can describe the kinds of problems you know how to solve. The CV sells capability; you will prove it again in the current process.',
        },
        {
          type: 'h2',
          text: 'Seniority: how not to look inflated or undervalued',
        },
        {
          type: 'p',
          text: 'Senior is not a longer acronym list—it is ownership, mentoring, design decisions, and problem type. A mid with strong impact beats a “senior” who only listed frameworks. In bullets, show decisions (“chose X over Y for latency”) when true.',
        },
        {
          type: 'p',
          text: 'If you come from a bootcamp or are switching into tech, do not compete by inventing years. Compete with solid projects, clear contributions, and a summary that states real level (“Junior developer with a background in…”). Honesty speeds matches to the right openings.',
        },
        {
          type: 'h2',
          text: 'Bilingual CVs and international remote roles',
        },
        {
          type: 'p',
          text: 'For remote-first roles, English is often the CV language even if you live in Spain. Keep a Spanish version for local companies. Ensure stack and achievements match on facts. ClarCV lets you start from the same file and generate clear outputs in the language each job asks for.',
        },
        {
          type: 'p',
          text: 'Include time zone or city when collaboration depends on it. “Madrid (CET) · remote” prevents misunderstandings. If you have work rights in certain countries, a discreet contact line can unlock mobility filters.',
        },
        {
          type: 'p',
          text: 'When you tailor the CV to a job ad, do not only reorder skills: move up the experience bullet that best proves the ad’s number-one requirement. ATS counts keywords; hiring managers count evidence. You need both layers on the same page.',
        },
        {
          type: 'h2',
          text: 'Metrics that work (and how to estimate them honestly)',
        },
        {
          type: 'p',
          text: 'If you lack a perfect dashboard, you can still quantify: number of services, release frequency, size of a migrated monolith, new-hire onboarding time, P1 tickets per month, test coverage, cloud cost before/after. Round honestly and be ready to explain the source in interview.',
        },
        {
          type: 'p',
          text: 'Avoid empty vanity metrics (“I wrote 50,000 lines of code”). Prefer outcomes: fewer incidents, faster delivery, better DX, savings. If impact was shared, say so: “with 3 engineers” does not reduce credit—it adds credibility.',
        },
        {
          type: 'ul',
          items: [
            'Latency, error rate, uptime, throughput.',
            'Internal adoption of a tool or library.',
            'Build/CI time and flaky tests removed.',
            'Migrations completed without downtime.',
            'Mentoring: people, duration, outcome (promotion, autonomy).',
          ],
        },
        {
          type: 'p',
          text: 'A great tech CV fits in one glance: right stack up top, evidence below. If a staff engineer can imagine working with you after reading it, it is ready. If they only see a logo soup, keep editing. ClarCV helps clean structure; honest metrics are your competitive edge.',
        },
        {
          type: 'p',
          text: 'Keep a private “brag document” of wins each quarter so the next CV rewrite is fast. Tailoring becomes selection, not archaeology. That habit alone separates candidates who update yearly from those who update when opportunity appears.',
        },
        {
          type: 'h2',
          text: 'What technical hiring managers scan in 60 seconds',
        },
        {
          type: 'p',
          text: 'Recent trajectory, aligned stack, ownership signals, and clear writing. If those four are present, they go deeper. If the CV looks like a course inventory with no impact, they move on. That is why every section must earn its space: less noise, more proof.',
        },
        {
          type: 'p',
          text: 'Include links only if they load fast and show current work. A down portfolio or a GitHub with no recent commits hurts more than no link. Keep a README on the main repo and a minimal demo if the role is frontend or full-stack.',
        },
        {
          type: 'p',
          text: 'Close the loop: tailor, export, apply, note feedback. A tech CV is not a monument; it is a continuous-iteration document. ClarCV shortens the formatting cycle so you can invest time in evidence that actually changes outcomes. After every export, confirm the PDF still has selectable text before you apply.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I list every technology I have touched?',
              a: 'No. Prioritize relevant and recent ones. Mention others only if the ad asks and you can defend them.',
            },
            {
              q: 'Is GitHub mandatory?',
              a: 'No, but it helps for hands-on roles when it shows real work. Closed-source work plus measurable achievements also works.',
            },
            {
              q: 'One or two pages for tech?',
              a: 'Junior: one. Mid/senior with dense impact: two can be fine. Avoid padding with irrelevant tools.',
            },
            {
              q: 'How do I show soft skills on a tech CV?',
              a: 'Through bullets: mentoring, tech leadership, partnering with product, incident communication—not an empty adjective list.',
            },
            {
              q: 'Can I use one version for every job?',
              a: 'A base version yes; skills and summary should move with the ad. Short tailoring improves ATS match and the first human read.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'primer-empleo-cv',
    date: '2026-08-07',
    category: 'career',
    related: ['cv-sin-experiencia', 'cv-para-practicas', 'tfg-tfm-en-el-cv'],
    es: {
      title: 'CV para el primer empleo: cómo destacar sin trayectoria larga',
      description:
        'Guía para estudiantes y juniors que buscan su primer trabajo: qué poner, cómo redactar logros y cómo evitar el CV vacío o inventado.',
      readMinutes: 14,
      sections: [
        {
          type: 'p',
          text: 'El primer empleo no exige un CV de diez años. Exige una historia creíble de potencial + prueba. Prácticas, proyectos, voluntariado, trabajos de media jornada, TFG, hackathons o freelance pequeño cuentan si los redactas con claridad. El error no es “tener poco”: es dejar la página en blanco o rellenarla de adjetivos.',
        },
        {
          type: 'p',
          text: 'Reclutadores de perfiles junior saben leer señales tempranas: responsabilidad, aprendizaje aplicado, comunicación y encaje con el rol. Tu trabajo es hacer esas señales obvias en una página limpia y ATS-friendly.',
        },
        {
          type: 'h2',
          text: 'Qué estructura usar cuando aún no hay “carrera”',
        },
        {
          type: 'p',
          text: 'Prueba este orden: contacto, resumen junior orientado al puesto, formación (si es tu ancla), experiencia (incluye prácticas y roles no formales relevantes), proyectos, skills, idiomas. Si tienes prácticas fuertes, sube experiencia por encima de formación.',
        },
        {
          type: 'ul',
          items: [
            'Una página suele ser suficiente y preferible.',
            'Márgenes y tipografía legibles: no reduzcas a 8 pt para “meter más”.',
            'Secciones estándar para ATS: Experiencia, Educación, Habilidades.',
            'Enlaces a portfolio, GitHub o LinkedIn solo si están cuidados.',
          ],
        },
        {
          type: 'h2',
          text: 'Resumen para primer empleo (sin clichés)',
        },
        {
          type: 'p',
          text: 'Antes: “Estudiante motivado busca oportunidad para crecer y aportar valor a la empresa.” Después: “Graduado en ADE con prácticas en control de gestión. Domino Excel avanzado y Power BI; busco rol junior de analista donde mejorar reporting y calidad de datos.” Específico, honesto, orientado.',
        },
        {
          type: 'p',
          text: 'Fórmula: [situación actual] + [prueba concreta] + [tipo de rol] + [aporte]. Evita “apasionado”, “dinámico” y “proactivo” si no van acompañados de ejemplo.',
        },
        {
          type: 'h2',
          text: 'Cómo convertir prácticas y proyectos en logros',
        },
        {
          type: 'p',
          text: 'Aunque no tengas P&L a tu cargo, tienes alcance. “Atendí el mostrador” puede ser “Gestioné atención presencial en picos de +80 clientes/día y resolví incidencias de stock con el equipo de almacén.” “Hice un trabajo de clase” puede ser “Analicé 3 años de datos públicos de movilidad y presenté recomendaciones a un panel de 5 docentes.”',
        },
        {
          type: 'ul',
          items: [
            'Incluye herramientas usadas (Excel, Figma, Python, Salesforce…).',
            'Menciona colaboración: equipo, cliente, tutor, usuarios.',
            'Añade frecuencia o volumen cuando exista.',
            'Cuenta el resultado: entrega a tiempo, error reducido, aprendizaje aplicado en producción.',
          ],
        },
        {
          type: 'h2',
          text: 'Qué sí poner si “no tengo experiencia”',
        },
        {
          type: 'ul',
          items: [
            'Prácticas curriculares o extracurriculares.',
            'Trabajos de verano, hostelería, retail, tutoring (transfieren soft skills reales).',
            'Voluntariado con responsabilidad (coordinación, caja, comunicación).',
            'Proyectos personales con entregable visible.',
            'Cursos con proyecto final, no solo el certificado.',
            'Premios, becas o competiciones relevantes.',
          ],
        },
        {
          type: 'p',
          text: 'No inventes puestos. Sí puedes enmarcar roles informales con título claro: “Tutor particular de matemáticas”, “Community volunteer – logística de eventos”. La honestidad se nota —y se verifica.',
        },
        {
          type: 'h2',
          text: 'Formación: cómo no desperdiciar espacio',
        },
        {
          type: 'p',
          text: 'Incluye título, centro, fechas y, si ayuda, promedio o menciones solo cuando sean fuertes. Asignaturas relevantes: 3–5 máximo alineadas a la oferta. El TFG/TFM merece una línea si demuestra método o herramienta del puesto.',
        },
        {
          type: 'p',
          text: 'Si aún estudias, pon “En curso” y fecha prevista. Si cambiaste de carrera, una línea breve evita confusión sin dramatizar.',
        },
        {
          type: 'h2',
          text: 'Skills para juniors: menos lista, más verdad',
        },
        {
          type: 'p',
          text: 'Lista habilidades que puedes demostrar en una prueba o entrevista. Separar “hard” y “soft” está bien si las soft están respaldadas (“trabajo en equipo” → viñeta de proyecto grupal con rol). Evita barras de 1 a 5 estrellas: dicen poco y parecen infantiles en muchos sectores.',
        },
        {
          type: 'h2',
          text: 'Errores que tumban candidaturas junior',
        },
        {
          type: 'ul',
          items: [
            'CV genérico enviado a 50 ofertas distintas sin adaptar una línea.',
            'Diseño imposible de parsear (PDF imagen, columnas rotas).',
            'Foto de fiesta o selfie informal cuando el mercado espera foto profesional.',
            'Exagerar títulos (“CEO de mi proyecto de clase”).',
            'Ortografía descuidada: señal brutal en primer filtro humano.',
          ],
        },
        {
          type: 'p',
          text: 'Antes de enviar, pasa la checklist rápida: contacto, resumen alineado, 3 logros concretos, skills verdaderas, PDF con texto seleccionable. ClarCV puede ayudarte a clarificar tu archivo inicial y aplicar una plantilla ATS; tú aportas la verdad de tus prácticas y proyectos.',
        },
        {
          type: 'h2',
          text: 'Plan de 7 días para tu primer CV sólido',
        },
        {
          type: 'ul',
          items: [
            'Día 1–2: inventaría prácticas, proyectos y skills demostrables.',
            'Día 3: escribe resumen + 6–10 viñetas fuertes.',
            'Día 4: elige plantilla ATS y exporta PDF limpio.',
            'Día 5: alinea LinkedIn con el mismo relato.',
            'Día 6: adapta una versión a 2 ofertas reales.',
            'Día 7: pide feedback a un mentor y corrige solo lo confuso.',
          ],
        },
        {
          type: 'h2',
          text: 'Cómo hablar de notas, becas y extracurriculares',
        },
        {
          type: 'p',
          text: 'Incluye promedio solo si es claramente fuerte en tu contexto (por ejemplo, top del curso o requisito de la oferta). Una beca competitiva o un premio nacional sí aportan. El club de debate, el equipo deportivo universitario o la asociación de estudiantes cuentan si tuviste rol (tesorería, organización de eventos, captación).',
        },
        {
          type: 'p',
          text: 'Redáctalos como experiencia breve: título del rol, organización, fechas, 1–2 viñetas de impacto. “Miembro” sin responsabilidad aporta poco; “Coordiné a 12 voluntarios en la feria de empleo con 40 empresas” sí.',
        },
        {
          type: 'h2',
          text: 'Primera búsqueda: volumen con calidad',
        },
        {
          type: 'p',
          text: 'No envíes 100 veces el mismo PDF. Elige 10–15 ofertas realistas por semana, adapta resumen y skills, y registra a qué versión enviaste. Esa disciplina junior impresiona más que el spray and pray. Usa la checklist rápida en cada envío.',
        },
        {
          type: 'p',
          text: 'Prepara también 3 historias STAR a partir de tus viñetas: conflicto en un proyecto, deadline, feedback difícil. El CV abre la puerta; las historias cierran la entrevista. Si ClarCV te ayudó a clarificar logros, usa esas mismas frases como base oral.',
        },
        {
          type: 'h2',
          text: 'Qué hacer si aún no tienes prácticas',
        },
        {
          type: 'p',
          text: 'Crea prueba en 2–4 semanas: un proyecto guiado del sector, un voluntariado corto, o un reto público (dataset, redesign, campaña simulada). Documéntalo y súbelo al CV. Esperar “experiencia para ganar experiencia” sin construir evidencia alarga el limbo.',
        },
        {
          type: 'p',
          text: 'Sé transparente en entrevistas sobre tu nivel. Las empresas que contratan primer empleo buscan potencial y actitud de aprendizaje. Un CV hinchado se cae en la primera pregunta técnica o situacional.',
        },
        {
          type: 'p',
          text: 'Si te rechazan sin feedback, no asumas que “no tienes nada que ofrecer”. Revisa si el archivo era parseable, si el resumen apuntaba al rol correcto y si aplicaste a vacantes realistas. Ajusta una variable por ciclo. La mejora compuesta de un CV junior bien iterado es enorme en tres meses.',
        },
        {
          type: 'h2',
          text: 'Ejemplos antes/después de viñetas junior',
        },
        {
          type: 'p',
          text: 'Antes: “Responsable de redes sociales de la asociación.” Después: “Planifiqué y publiqué 3 posts/semana en Instagram y LinkedIn; crecí la comunidad un 22% en un cuatrimestre y coordiné a 2 voluntarios de diseño.” Antes: “Prácticas en contabilidad.” Después: “Apoyé el cierre mensual: concilié 120+ asientos y documenté un checklist que redujo errores de imputación.”',
        },
        {
          type: 'p',
          text: 'Antes: “Proyecto de fin de grado sobre app móvil.” Después: “Diseñé e implementé una app Flutter con autenticación y 4 pantallas clave; probé con 15 usuarios y prioricé mejoras de usabilidad.” La diferencia no es inventar: es especificar.',
        },
        {
          type: 'ul',
          items: [
            'Sustituye “ayudé” por el verbo de tu aporte real.',
            'Añade herramienta solo si la usaste de verdad.',
            'Si no hay %, usa volumen, plazo o entregable.',
            'Elimina asignaturas irrelevantes para ganar espacio a proyectos.',
          ],
        },
        {
          type: 'p',
          text: 'Tu primer CV es un producto vivo. Cada mes de búsqueda debería dejarlo un 5% más claro. Con plantilla ATS, resumen nítido y tres historias STAR, compites por encima de muchos peers con más “relleno”. ClarCV te quita fricción de formato para que inviertas energía en evidencia real.',
        },
        {
          type: 'h2',
          text: 'Padres, mentors y feedback útil (sin reescribirte)',
        },
        {
          type: 'p',
          text: 'Pide feedback concreto: “¿Qué no entiendes?” o “¿A qué puesto parece que aplico?” Evita opiniones genéricas de estilo que te empujen a un diseño no ATS. Filtra consejos de quienes no contratan en tu sector. Un mentor de RR. HH. o un junior recién colocado suele aportar más que cinco opiniones contradictorias.',
        },
        {
          type: 'p',
          text: 'Si alguien sugiere inventar experiencia “porque todo el mundo lo hace”, ignóralo. El coste reputacional es alto y las entrevistas lo detectan. Mejor un CV corto y verdadero que uno largo e indefendible.',
        },
        {
          type: 'p',
          text: 'Celebra versiones: v1 legible, v2 con logros, v3 adaptada a tu sector objetivo. Ese progreso visible mantiene la motivación en una búsqueda que puede durar meses. ClarCV hace más barato (en tiempo) pasar de v1 a v3 sin pelearte con márgenes y columnas.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Puedo dejar la sección de experiencia vacía?',
              a: 'Mejor no. Rellénala con prácticas, proyectos o roles informales relevantes. Una sección vacía invita a descartar.',
            },
            {
              q: '¿Debo incluir trabajos de hostelería o retail?',
              a: 'Sí, si demuestran responsabilidad, turnos, clientes o caja—sobre todo si aún no tienes prácticas del sector objetivo.',
            },
            {
              q: '¿Foto sí o no en el primer CV?',
              a: 'Depende del país y la oferta. En ES/LATAM suele ser habitual; en EE. UU./UK, normalmente no. Si la pones, que sea profesional.',
            },
            {
              q: '¿Cuánto debe durar el resumen?',
              a: '3–4 líneas. Suficiente para orientar, corto para escanear.',
            },
            {
              q: '¿Cómo ayuda ClarCV a un perfil junior?',
              a: 'Subes tu PDF/Word, mejoras claridad y estructura, y generas una plantilla ATS legible. El contenido real (prácticas/proyectos) sigue siendo tuyo.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'First-job CV: how to stand out without a long track record',
      description:
        'A guide for students and juniors hunting their first role: what to include, how to write achievements, and how to avoid an empty or invented résumé.',
      readMinutes: 14,
      sections: [
        {
          type: 'p',
          text: 'A first job does not require a ten-year CV. It requires a credible story of potential plus proof. Internships, projects, volunteering, part-time jobs, theses, hackathons, or small freelance work count when you write them clearly. The mistake is not “having little”—it is leaving the page blank or filling it with adjectives.',
        },
        {
          type: 'p',
          text: 'Junior recruiters know how to read early signals: responsibility, applied learning, communication, and role fit. Your job is to make those signals obvious on one clean, ATS-friendly page.',
        },
        {
          type: 'h2',
          text: 'What structure to use when you do not have a “career” yet',
        },
        {
          type: 'p',
          text: 'Try this order: contact, junior summary aimed at the role, education (if it is your anchor), experience (include internships and relevant non-formal roles), projects, skills, languages. If you have strong internships, place experience above education.',
        },
        {
          type: 'ul',
          items: [
            'One page is usually enough and preferred.',
            'Readable margins and type: do not shrink to 8 pt to “fit more.”',
            'Standard ATS sections: Experience, Education, Skills.',
            'Links to portfolio, GitHub, or LinkedIn only if they are cared for.',
          ],
        },
        {
          type: 'h2',
          text: 'First-job summary (without clichés)',
        },
        {
          type: 'p',
          text: 'Before: “Motivated student seeking an opportunity to grow and add value.” After: “Business graduate with a controlling internship. Strong Excel and Power BI; seeking a junior analyst role focused on reporting and data quality.” Specific, honest, directed.',
        },
        {
          type: 'p',
          text: 'Formula: [current situation] + [concrete proof] + [role type] + [what you bring]. Avoid “passionate,” “dynamic,” and “proactive” unless an example follows.',
        },
        {
          type: 'h2',
          text: 'Turning internships and projects into achievements',
        },
        {
          type: 'p',
          text: 'Even without P&L ownership, you have scope. “Worked the counter” can become “Handled in-person service during peaks of 80+ customers/day and resolved stock issues with the warehouse team.” “Did a class project” can become “Analyzed 3 years of public mobility data and presented recommendations to a 5-instructor panel.”',
        },
        {
          type: 'ul',
          items: [
            'Include tools used (Excel, Figma, Python, Salesforce…).',
            'Mention collaboration: team, client, tutor, users.',
            'Add frequency or volume when it exists.',
            'State the outcome: on-time delivery, fewer errors, learning applied in production.',
          ],
        },
        {
          type: 'h2',
          text: 'What to include if you “have no experience”',
        },
        {
          type: 'ul',
          items: [
            'Credit or extracurricular internships.',
            'Summer jobs, hospitality, retail, tutoring (they transfer real soft skills).',
            'Volunteering with responsibility (coordination, cash, communications).',
            'Personal projects with a visible deliverable.',
            'Courses with a final project—not certificate-only lines.',
            'Relevant awards, scholarships, or competitions.',
          ],
        },
        {
          type: 'p',
          text: 'Do not invent jobs. You can frame informal roles with a clear title: “Private math tutor,” “Community volunteer – event logistics.” Honesty shows—and gets checked.',
        },
        {
          type: 'h2',
          text: 'Education: how not to waste space',
        },
        {
          type: 'p',
          text: 'Include degree, school, dates, and GPA/honors only when they are strong. Relevant coursework: 3–5 items max aligned to the ad. A thesis deserves a line if it shows methods or tools used in the job.',
        },
        {
          type: 'p',
          text: 'If you are still studying, write “In progress” and expected date. If you changed majors, one brief line prevents confusion without drama.',
        },
        {
          type: 'h2',
          text: 'Skills for juniors: fewer items, more truth',
        },
        {
          type: 'p',
          text: 'List skills you can demonstrate in a test or interview. Separating hard and soft skills is fine if soft skills are backed (“teamwork” → group project bullet with your role). Avoid 1–5 star bars: they say little and look childish in many industries.',
        },
        {
          type: 'h2',
          text: 'Mistakes that sink junior applications',
        },
        {
          type: 'ul',
          items: [
            'One generic CV sent to 50 different ads without changing a line.',
            'Unparsable design (image PDF, broken columns).',
            'Party photo or casual selfie when the market expects a professional headshot.',
            'Inflated titles (“CEO of my class project”).',
            'Careless spelling: a brutal signal in the first human screen.',
          ],
        },
        {
          type: 'p',
          text: 'Before you apply, run a quick checklist: contact, aligned summary, 3 concrete achievements, true skills, selectable-text PDF. ClarCV can help clarify your first file and apply an ATS template; you still provide the truth of your internships and projects.',
        },
        {
          type: 'h2',
          text: 'A 7-day plan for a solid first CV',
        },
        {
          type: 'ul',
          items: [
            'Day 1–2: inventory internships, projects, and demonstrable skills.',
            'Day 3: write summary + 6–10 strong bullets.',
            'Day 4: choose an ATS template and export a clean PDF.',
            'Day 5: align LinkedIn with the same story.',
            'Day 6: tailor one version to 2 real job ads.',
            'Day 7: get mentor feedback and fix only what confuses.',
          ],
        },
        {
          type: 'h2',
          text: 'How to talk about grades, scholarships, and extracurriculars',
        },
        {
          type: 'p',
          text: 'Include GPA only if it is clearly strong in your context (for example, top of class or required by the ad). A competitive scholarship or national award helps. Debate club, university sports, or student associations count if you had a role (treasury, event ops, outreach).',
        },
        {
          type: 'p',
          text: 'Write them as short experience: role title, organization, dates, 1–2 impact bullets. “Member” with no responsibility adds little; “Coordinated 12 volunteers at a career fair with 40 employers” does.',
        },
        {
          type: 'h2',
          text: 'First search: volume with quality',
        },
        {
          type: 'p',
          text: 'Do not send the same PDF 100 times. Pick 10–15 realistic ads per week, tune summary and skills, and log which version you sent. That junior discipline impresses more than spray-and-pray. Use the fast checklist on every submission.',
        },
        {
          type: 'p',
          text: 'Also prepare 3 STAR stories from your bullets: project conflict, a deadline, difficult feedback. The CV opens the door; stories close the interview. If ClarCV helped clarify achievements, use those same phrases as your spoken base.',
        },
        {
          type: 'h2',
          text: 'What to do if you still have no internship',
        },
        {
          type: 'p',
          text: 'Create proof in 2–4 weeks: a guided sector project, short volunteering, or a public challenge (dataset, redesign, simulated campaign). Document it and add it to the CV. Waiting for “experience to get experience” without building evidence extends the limbo.',
        },
        {
          type: 'p',
          text: 'Be transparent in interviews about your level. Employers hiring for first roles look for potential and learning attitude. An inflated CV collapses on the first technical or situational question.',
        },
        {
          type: 'p',
          text: 'If you are rejected without feedback, do not assume you “have nothing to offer.” Check whether the file was parsable, whether the summary aimed at the right role, and whether the jobs were realistic. Adjust one variable per cycle. Compound improvement on a well-iterated junior CV is huge over three months.',
        },
        {
          type: 'h2',
          text: 'Before/after examples of junior bullets',
        },
        {
          type: 'p',
          text: 'Before: “Responsible for the association’s social media.” After: “Planned and published 3 posts/week on Instagram and LinkedIn; grew the community 22% in one term and coordinated 2 design volunteers.” Before: “Accounting internship.” After: “Supported month-end close: reconciled 120+ entries and documented a checklist that reduced posting errors.”',
        },
        {
          type: 'p',
          text: 'Before: “Degree final project on a mobile app.” After: “Designed and built a Flutter app with auth and 4 key screens; tested with 15 users and prioritized usability fixes.” The difference is not invention—it is specificity.',
        },
        {
          type: 'ul',
          items: [
            'Replace “helped” with the verb for your real contribution.',
            'Add a tool only if you truly used it.',
            'If you lack %, use volume, timeline, or deliverable.',
            'Cut irrelevant coursework to make room for projects.',
          ],
        },
        {
          type: 'p',
          text: 'Your first CV is a living product. Each month of search should make it about 5% clearer. With an ATS template, a sharp summary, and three STAR stories, you compete above many peers with more “filler.” ClarCV removes formatting friction so you invest energy in real evidence.',
        },
        {
          type: 'p',
          text: 'Ask professors, internship tutors, or club leads for one-line testimonials you can paraphrase as outcomes—not quotes on the CV, but facts you can defend. External validation makes thin experience feel solid without exaggeration.',
        },
        {
          type: 'h2',
          text: 'Parents, mentors, and useful feedback (without rewriting you)',
        },
        {
          type: 'p',
          text: 'Ask for concrete feedback: “What do you not understand?” or “What role does this seem aimed at?” Avoid generic style opinions that push you into a non-ATS design. Filter advice from people who do not hire in your sector. An HR mentor or a recently hired junior often helps more than five contradictory opinions.',
        },
        {
          type: 'p',
          text: 'If someone suggests inventing experience “because everyone does it,” ignore them. The reputational cost is high and interviews catch it. A short true CV beats a long indefensible one.',
        },
        {
          type: 'p',
          text: 'Celebrate versions: v1 readable, v2 with achievements, v3 tailored to your target sector. That visible progress sustains motivation in a search that can last months. ClarCV makes it cheaper (in time) to move from v1 to v3 without fighting margins and columns. Keep each version dated so you always know which file you sent where.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Can I leave the experience section empty?',
              a: 'Preferably not. Fill it with internships, projects, or relevant informal roles. An empty section invites rejection.',
            },
            {
              q: 'Should I include hospitality or retail jobs?',
              a: 'Yes, if they show responsibility, shifts, customers, or cash handling—especially before you have target-sector internships.',
            },
            {
              q: 'Photo or no photo on a first CV?',
              a: 'Depends on country and posting. In ES/LATAM it is often common; in the US/UK, usually no. If you include one, keep it professional.',
            },
            {
              q: 'How long should the summary be?',
              a: '3–4 lines. Enough to orient, short enough to scan.',
            },
            {
              q: 'How does ClarCV help a junior profile?',
              a: 'Upload your PDF/Word, improve clarity and structure, and generate a readable ATS template. Real content (internships/projects) remains yours.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cambiar-de-sector-cv',
    date: '2026-08-07',
    category: 'career',
    related: ['hueco-laboral-en-el-cv', 'adaptar-cv-a-una-oferta', 'skills-blandas-y-duras'],
    es: {
      title: 'Cambiar de sector con el CV: cómo reescribir tu experiencia',
      description:
        'Estrategia práctica para pivotar de industria: skills transferibles, resumen nuevo, logros reencuadrados y errores que sabotean el cambio.',
      readMinutes: 15,
      sections: [
        {
          type: 'p',
          text: 'Cambiar de sector no se soluciona con un CV idéntico y un “busco nuevos retos” en el resumen. El reclutador del sector destino no conoce tus siglas internas ni por qué tu logro importa. Tú debes traducir. Misma carrera, nuevo marco: problemas que resolviste → equivalencias en el mercado al que vas.',
        },
        {
          type: 'p',
          text: 'Un buen CV de transición hace tres cosas: declara el rumbo sin ambigüedad, demuestra transferibles con evidencia, y reduce el ruido del pasado irrelevante. No borra tu historia: la reedita para una audiencia nueva.',
        },
        {
          type: 'h2',
          text: 'Define el destino antes de reescribir',
        },
        {
          type: 'p',
          text: 'Elige 1–2 familias de rol (por ejemplo: customer success en SaaS, o operaciones en retail). Lee 10 ofertas y anota requisitos repetidos. Ese vocabulario será tu nueva lengua. Si apuntas a cinco sectores a la vez, el CV parecerá confuso y el ATS no sabrá en qué cubo meterte.',
        },
        {
          type: 'ul',
          items: [
            'Lista requisitos que ya cumples.',
            'Lista gaps honestos (herramientas, certificaciones, dominio).',
            'Decide qué gap puedes cerrar en 30–60 días (curso + proyecto).',
            'Ajusta expectativas de seniority: a veces el pivot implica un escalón lateral.',
          ],
        },
        {
          type: 'h2',
          text: 'El resumen: tu puente narrativo',
        },
        {
          type: 'p',
          text: 'Antes (débil): “Profesional de banca con 8 años busca oportunidad en el mundo digital.” Después (fuerte): “Especialista en relación con clientes con 8 años en banca retail. Experiencia en onboarding, retención y manejo de objeciones; transición a Customer Success en SaaS B2B.” El segundo dice de dónde vienes, qué traes y a dónde vas.',
        },
        {
          type: 'p',
          text: 'No escribas una carta de motivación dentro del CV. 3–5 líneas bastan. Si el cambio es radical, una línea de “Proyecto reciente” o certificación debajo del resumen refuerza seriedad.',
        },
        {
          type: 'h2',
          text: 'Reencuadrar logros: el método de traducción',
        },
        {
          type: 'p',
          text: 'Toma cada viñeta fuerte y pregúntate: ¿qué capacidad genérica demuestra? Liderazgo de stakeholders, análisis, ventas, cumplimiento, mejora de procesos, formación de equipos, gestión de crisis… Luego reescríbela con el vocabulario del sector destino, sin falsificar el hecho.',
        },
        {
          type: 'p',
          text: 'Ejemplo: de “Reduje reclamaciones de tarjeta un 18% rediseñando el script de call center” a “Reduje un 18% las incidencias de postventa rediseñando el playbook de soporte y formaciones semanales al equipo.” El hecho es el mismo; el marco habla a Customer Success u Operations.',
        },
        {
          type: 'ul',
          items: [
            'Sustituye jerga solo de tu industria por términos transversales.',
            'Mantén métricas: son tu moneda de credibilidad.',
            'Destaca herramientas que existan en ambos mundos (Excel, CRM, SQL, SAP…).',
            'Quita o comprime lo que no aporta al destino.',
          ],
        },
        {
          type: 'h2',
          text: 'Skills transferibles vs skills a construir',
        },
        {
          type: 'p',
          text: 'Separa en dos bloques mentales: lo que ya puedes probar en entrevista mañana, y lo que estás aprendiendo. En el CV, prioriza lo primero. Lo segundo puede aparecer como “en progreso” solo si es relevante y no diluye.',
        },
        {
          type: 'p',
          text: 'Soft skills no bastan solas (“adaptabilidad”). Mejor: “Coordiné un cambio de proveedor en 6 semanas con 3 equipos y sin interrupción de servicio.” Eso es adaptabilidad demostrada.',
        },
        {
          type: 'h2',
          text: 'Orden y énfasis: qué subir y qué bajar',
        },
        {
          type: 'p',
          text: 'Sube proyectos, formaciones o roles internos que se parezcan al destino. Baja responsabilidades muy específicas del sector antiguo si ocupan espacio sin traducción. A veces un apartado “Proyectos relevantes” cerca del resumen acelera el click mental del reclutador.',
        },
        {
          type: 'p',
          text: 'Las fechas deben seguir siendo verdaderas. No fabriques un historial “más tech” o “más comercial”. El pivot se defiende con traducción y aprendizaje, no con ficción.',
        },
        {
          type: 'h2',
          text: 'LinkedIn y CV en un cambio de sector',
        },
        {
          type: 'p',
          text: 'Actualiza titular y About el mismo día que el resumen del CV. Publica o destaca un proyecto del nuevo rumbo. Cuando el reclutador compare ambos, debe ver la misma dirección. Inconsistencia = riesgo.',
        },
        {
          type: 'h2',
          text: 'Errores que sabotean el pivot',
        },
        {
          type: 'ul',
          items: [
            'Resumen tibio que no elige destino.',
            'Copiar keywords de la oferta que no puedes defender.',
            'Dejar el 80% del CV en jerga del sector antiguo.',
            'Ocultar el pasado por vergüenza: genera gaps sospechosos.',
            'Pretender seniority alto en un campo donde aún eres junior de dominio.',
          ],
        },
        {
          type: 'p',
          text: 'Usa ClarCV para partir de tu PDF/Word actual, clarificar logros y generar una plantilla limpia; luego haz la pasada de “traducción” al vocabulario del sector. Pro a 7 €/mes si vas a iterar varias versiones de pivot sin fricción.',
        },
        {
          type: 'h2',
          text: 'Mini plan de reescritura (un fin de semana)',
        },
        {
          type: 'ul',
          items: [
            'Sábado mañana: 10 ofertas → nube de keywords y requisitos.',
            'Sábado tarde: nuevo resumen + selección de 8–12 viñetas transferibles.',
            'Domingo mañana: reescritura con vocabulario destino + skills.',
            'Domingo tarde: PDF ATS, alineación LinkedIn, prueba con un mentor del sector.',
          ],
        },
        {
          type: 'h2',
          text: 'Mapa de equivalencias: ejemplo banca → SaaS',
        },
        {
          type: 'p',
          text: 'Clientes prioritarios → cuentas o segmentos. Cumplimiento y KYC → atención al riesgo y proceso. Campañas de retención de tarjetas → playbooks de adopción. Formación a oficinas → enablement. Cuando reescribes con este mapa, el hiring manager de Customer Success reconoce patrones aunque nunca haya trabajado en un banco.',
        },
        {
          type: 'p',
          text: 'Haz tu propio mapa en una hoja: columna A (logro antiguo), columna B (capacidad), columna C (frase destino). Si la columna C suena forzada, el logro quizá no deba ir en la primera página del CV de pivot.',
        },
        {
          type: 'h2',
          text: 'Redes, referencias e historias de entrevista',
        },
        {
          type: 'p',
          text: 'El CV abre; las conversaciones cierran. Habla con 3–5 personas del sector destino y ajusta el vocabulario según lo que ellos digan que “suena a insider”. Pide referencias que puedan hablar de transferibles (liderazgo, analítica, ownership), no solo del sector antiguo.',
        },
        {
          type: 'p',
          text: 'Prepara la respuesta a “¿por qué el cambio?” en 60 segundos alineada al resumen del CV. Si el PDF dice Customer Success y en entrevista improvisas “quizá producto o data”, pierdes coherencia. Misma historia en documento, LinkedIn y oral.',
        },
        {
          type: 'h2',
          text: 'Cuándo un CV funcional ayuda (y cuándo no)',
        },
        {
          type: 'p',
          text: 'El CV funcional (agrupado por capacidades) a veces se sugiere en pivots, pero muchos ATS y reclutadores desconfían porque oculta la cronología. Preferible: cronológico inverso + resumen puente + viñetas traducidas. Reserva lo funcional para un anexo de portfolio, no para sustituir fechas.',
        },
        {
          type: 'p',
          text: 'Si el gap o el cambio es muy grande, un proyecto puente visible vale más que un formato exótico. ClarCV mantiene el formato ATS mientras tú haces el trabajo estratégico de traducción: esa combinación suele ser la más efectiva.',
        },
        {
          type: 'p',
          text: 'Mide progreso del pivot como producto: tasa de respuestas, calidad de entrevistas, feedback cualitativo. Si nadie responde, el problema puede ser keywords o claridad, no tu valor intrínseco. Itera el resumen y las tres primeras viñetas antes de rendirte o de “aplicar a todo”.',
        },
        {
          type: 'h2',
          text: 'Señales de que tu CV de pivot ya está listo',
        },
        {
          type: 'p',
          text: 'Un lector del sector destino entiende tu resumen sin preguntar “¿pero de qué trabajabas?”. Las tres primeras viñetas suenan al puesto. Las skills coinciden con el anuncio sin inventos. LinkedIn cuenta la misma historia. Has cerrado al menos un gap visible (curso + proyecto) y puedes hablar de él 90 segundos.',
        },
        {
          type: 'p',
          text: 'Si aún necesitas un párrafo largo para justificar el cambio dentro del CV, el resumen no está maduro. Condensa. La justificación profunda va a la entrevista y a la red; el documento solo debe hacer obvio el puente.',
        },
        {
          type: 'ul',
          items: [
            'Prueba A: alguien del destino resume tu perfil en una frase correcta.',
            'Prueba B: el ATS de un portal grande acepta el archivo sin rarezas.',
            'Prueba C: puedes adaptar el CV a una oferta en 10 minutos.',
            'Prueba D: no te da vergüenza enviar el PDF a un mentor del nuevo sector.',
          ],
        },
        {
          type: 'p',
          text: 'El cambio de sector es un proyecto de posicionamiento, no solo de diseño. ClarCV resuelve claridad y plantilla; tú resuelves traducción y prueba. Cuando ambas partes trabajan, el CV deja de pedir permiso y empieza a abrir conversaciones.',
        },
        {
          type: 'p',
          text: 'Última idea práctica: guarda un “diccionario de pivot” personal con 20 términos del sector destino y tus equivalencias. Úsalo cada vez que adaptes el CV a una oferta. En seis semanas, escribirás en el idioma del mercado sin forzar. Ese diccionario, más una plantilla ATS y un resumen puente, es el kit mínimo viable del cambio de sector.',
        },
        {
          type: 'p',
          text: 'No esperes perfección antes de aplicar a roles puente. Aplica, aprende del silencio o del feedback, itera. El CV mejora con contacto real de mercado. ClarCV está para que esa iteración sea rápida, clara y justa en precio mientras construyes tu nueva narrativa profesional.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Debo ocultar mi sector anterior?',
              a: 'No. Tradúcelo. Ocultar genera dudas. El valor está en mostrar equivalencias claras.',
            },
            {
              q: '¿Es normal bajar de título al cambiar de sector?',
              a: 'A veces sí, especialmente si cambia el dominio técnico. Un lateral bien elegido acelera más que forzar un título inflado.',
            },
            {
              q: '¿Cuántas versiones del CV necesito?',
              a: 'Una base de pivot y 1–2 variantes por familia de rol. No hace falta una versión distinta por cada oferta, pero sí ajustes de resumen y skills.',
            },
            {
              q: '¿Qué peso tienen los cursos en un cambio de carrera?',
              a: 'Ayudan como señal de seriedad si incluyen proyecto aplicable. Un certificado solo, sin prueba, pesa poco.',
            },
            {
              q: '¿Cómo sé si mi traducción convence?',
              a: 'Si alguien del sector destino entiende tus viñetas sin conocer tu industria antigua, vas bien. Si pide “¿y eso qué significa?”, sigue traduciendo.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Career-change CV: how to rewrite your experience for a new industry',
      description:
        'A practical strategy to pivot industries: transferable skills, a new summary, reframed achievements, and mistakes that sabotage the switch.',
      readMinutes: 15,
      sections: [
        {
          type: 'p',
          text: 'Changing sectors is not solved with an identical CV and “seeking new challenges” in the summary. Destination-sector recruiters do not know your internal acronyms or why your win matters. You must translate. Same career, new frame: problems you solved → equivalents in the market you are entering.',
        },
        {
          type: 'p',
          text: 'A strong transition CV does three things: states direction without ambiguity, proves transferables with evidence, and cuts noise from irrelevant past detail. It does not erase your history—it reedits it for a new audience.',
        },
        {
          type: 'h2',
          text: 'Define the destination before you rewrite',
        },
        {
          type: 'p',
          text: 'Choose 1–2 role families (for example: customer success in SaaS, or operations in retail). Read 10 job ads and note repeated requirements. That vocabulary becomes your new language. If you aim at five sectors at once, the CV looks confused and the ATS will not know which bucket you belong in.',
        },
        {
          type: 'ul',
          items: [
            'List requirements you already meet.',
            'List honest gaps (tools, certifications, domain knowledge).',
            'Decide which gap you can close in 30–60 days (course + project).',
            'Adjust seniority expectations: pivots sometimes mean a lateral step.',
          ],
        },
        {
          type: 'h2',
          text: 'The summary: your narrative bridge',
        },
        {
          type: 'p',
          text: 'Before (weak): “Banking professional with 8 years seeking an opportunity in the digital world.” After (strong): “Customer-facing specialist with 8 years in retail banking. Experience in onboarding, retention, and objection handling; transitioning to Customer Success in B2B SaaS.” The second says where you come from, what you bring, and where you are going.',
        },
        {
          type: 'p',
          text: 'Do not write a cover letter inside the CV. 3–5 lines are enough. If the change is radical, one line under the summary about a recent project or certification reinforces seriousness.',
        },
        {
          type: 'h2',
          text: 'Reframing achievements: the translation method',
        },
        {
          type: 'p',
          text: 'Take each strong bullet and ask: what generic capability does it prove? Stakeholder leadership, analysis, sales, compliance, process improvement, team training, crisis management… Then rewrite it with destination-sector vocabulary without falsifying the fact.',
        },
        {
          type: 'p',
          text: 'Example: from “Cut card complaints 18% by redesigning the call-center script” to “Cut post-sale incidents 18% by redesigning the support playbook and running weekly team enablement.” Same fact; frame speaks to Customer Success or Operations.',
        },
        {
          type: 'ul',
          items: [
            'Replace industry-only jargon with cross-cutting terms.',
            'Keep metrics: they are your credibility currency.',
            'Highlight tools that exist in both worlds (Excel, CRM, SQL, SAP…).',
            'Cut or compress what does not serve the destination.',
          ],
        },
        {
          type: 'h2',
          text: 'Transferable skills vs skills to build',
        },
        {
          type: 'p',
          text: 'Split two mental buckets: what you can prove in an interview tomorrow, and what you are learning. On the CV, prioritize the first. The second can appear as “in progress” only if it is relevant and does not dilute.',
        },
        {
          type: 'p',
          text: 'Soft skills alone are not enough (“adaptability”). Better: “Coordinated a vendor switch in 6 weeks across 3 teams with no service interruption.” That is demonstrated adaptability.',
        },
        {
          type: 'h2',
          text: 'Order and emphasis: what to raise and what to lower',
        },
        {
          type: 'p',
          text: 'Raise projects, training, or internal roles that resemble the destination. Lower highly specific old-sector duties if they take space without translation. Sometimes a “Relevant projects” block near the summary speeds the recruiter’s mental click.',
        },
        {
          type: 'p',
          text: 'Dates must stay true. Do not fabricate a “more tech” or “more commercial” history. A pivot is defended with translation and learning, not fiction.',
        },
        {
          type: 'h2',
          text: 'LinkedIn and CV during a sector change',
        },
        {
          type: 'p',
          text: 'Update headline and About the same day you change the CV summary. Publish or feature a project for the new direction. When a recruiter compares both, they should see the same path. Inconsistency equals risk.',
        },
        {
          type: 'h2',
          text: 'Mistakes that sabotage the pivot',
        },
        {
          type: 'ul',
          items: [
            'A lukewarm summary that never chooses a destination.',
            'Copying job-ad keywords you cannot defend.',
            'Leaving 80% of the CV in old-sector jargon.',
            'Hiding the past out of embarrassment: it creates suspicious gaps.',
            'Claiming high seniority in a field where you are still domain-junior.',
          ],
        },
        {
          type: 'p',
          text: 'Use ClarCV to start from your current PDF/Word, clarify achievements, and generate a clean template; then do the “translation” pass into sector vocabulary. Pro at €7/month if you will iterate several pivot versions without friction.',
        },
        {
          type: 'h2',
          text: 'Mini rewrite plan (one weekend)',
        },
        {
          type: 'ul',
          items: [
            'Saturday morning: 10 job ads → keyword and requirement bank.',
            'Saturday afternoon: new summary + select 8–12 transferable bullets.',
            'Sunday morning: rewrite with destination vocabulary + skills.',
            'Sunday afternoon: ATS PDF, LinkedIn alignment, review with a mentor in the target sector.',
          ],
        },
        {
          type: 'h2',
          text: 'Equivalence map: banking → SaaS example',
        },
        {
          type: 'p',
          text: 'Priority clients → accounts or segments. Compliance and KYC → risk awareness and process. Card retention campaigns → adoption playbooks. Branch training → enablement. When you rewrite with this map, a Customer Success hiring manager recognizes patterns even without banking experience.',
        },
        {
          type: 'p',
          text: 'Build your own map on a sheet: column A (old win), column B (capability), column C (destination phrasing). If column C feels forced, that win may not belong on page one of the pivot CV.',
        },
        {
          type: 'h2',
          text: 'Networks, references, and interview stories',
        },
        {
          type: 'p',
          text: 'The CV opens; conversations close. Talk to 3–5 people in the destination sector and tune vocabulary to what they say “sounds like an insider.” Ask references who can speak to transferables (leadership, analytics, ownership), not only the old sector.',
        },
        {
          type: 'p',
          text: 'Prepare a 60-second “why the change?” answer aligned with the CV summary. If the PDF says Customer Success and in interview you improvise “maybe product or data,” you lose coherence. Same story in the document, LinkedIn, and speech.',
        },
        {
          type: 'h2',
          text: 'When a functional CV helps (and when it does not)',
        },
        {
          type: 'p',
          text: 'Functional CVs (grouped by capability) are sometimes suggested for pivots, but many ATS tools and recruiters distrust them because they hide chronology. Prefer reverse chronological + bridge summary + translated bullets. Save functional grouping for a portfolio annex, not as a date replacement.',
        },
        {
          type: 'p',
          text: 'If the gap or change is large, a visible bridge project beats an exotic format. ClarCV keeps the ATS format while you do the strategic translation work—that combination is usually the most effective.',
        },
        {
          type: 'p',
          text: 'Measure pivot progress like a product: reply rate, interview quality, qualitative feedback. If nobody replies, the issue may be keywords or clarity—not your intrinsic value. Iterate the summary and the first three bullets before you quit or “apply to everything.”',
        },
        {
          type: 'h2',
          text: 'Signals your pivot CV is ready',
        },
        {
          type: 'p',
          text: 'A reader in the destination sector understands your summary without asking “but what did you actually do?” The first three bullets sound like the role. Skills match the ad without invention. LinkedIn tells the same story. You closed at least one visible gap (course + project) and can speak about it for 90 seconds.',
        },
        {
          type: 'p',
          text: 'If you still need a long paragraph inside the CV to justify the change, the summary is not mature. Condense. Deep justification belongs in interviews and networking; the document only needs to make the bridge obvious.',
        },
        {
          type: 'ul',
          items: [
            'Test A: someone in the destination summarizes your profile in one correct sentence.',
            'Test B: a large-portal ATS accepts the file without oddities.',
            'Test C: you can tailor the CV to a job ad in 10 minutes.',
            'Test D: you are not embarrassed to send the PDF to a mentor in the new sector.',
          ],
        },
        {
          type: 'p',
          text: 'A sector change is a positioning project, not only a design task. ClarCV handles clarity and template; you handle translation and proof. When both parts work, the CV stops asking for permission and starts opening conversations.',
        },
        {
          type: 'p',
          text: 'Keep a changelog of pivot versions with dates and target role family. When a recruiter asks why your story sharpened over two months, you can show intentional iteration—another credibility signal that accidental applicants rarely have.',
        },
        {
          type: 'p',
          text: 'Final practical idea: keep a personal “pivot dictionary” with 20 destination-sector terms and your equivalences. Use it every time you tailor the CV to a job ad. In six weeks you will write in market language without forcing it. That dictionary, plus an ATS template and a bridge summary, is the minimum viable kit for a sector change.',
        },
        {
          type: 'p',
          text: 'Do not wait for perfection before applying to bridge roles. Apply, learn from silence or feedback, iterate. The CV improves through real market contact. ClarCV exists so that iteration stays fast, clear, and fairly priced while you build your new professional narrative.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I hide my previous sector?',
              a: 'No. Translate it. Hiding creates doubt. Value comes from clear equivalences.',
            },
            {
              q: 'Is it normal to take a lower title when changing sectors?',
              a: 'Sometimes yes, especially when domain knowledge changes. A smart lateral move can accelerate more than an inflated title.',
            },
            {
              q: 'How many CV versions do I need?',
              a: 'One pivot base and 1–2 variants per role family. Not one unique file per ad, but do tune summary and skills.',
            },
            {
              q: 'How much do courses weigh in a career change?',
              a: 'They help as a seriousness signal when they include an applicable project. A certificate alone, without proof, weighs little.',
            },
            {
              q: 'How do I know my translation works?',
              a: 'If someone in the destination sector understands your bullets without knowing your old industry, you are on track. If they ask “what does that mean?”, keep translating.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
];
